import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { useAuth } from '../../../app/auth';
import { Cliente } from '../../../types';
import {
  SorteoType,
  TicketItem,
  TicketItemData,
  MonazoTipo,
  PaymentMethod,
  SORTEO_CONFIG,
  MESES,
  calcularTotalItem,
  createEmptyTicketsBySorteo,
  getSorteoModalidad,
  formatNumber2,
  TicketsBySorteo,
} from '../types';
import { Modal, Button } from '../../../components/ui';
import { Trash2, Pencil, X, CheckCircle, User, Send, Printer, Clock } from 'lucide-react';
import { creditoService } from '../../../services/creditoService';
import { AnularTicketModal } from '../components/AnularTicketModal';
import { ReimpresionTicketModal } from '../components/ReimpresionTicketModal';
import { fetchAPI } from '../../../services/apiClient';
import { procesarPostVenta, procesarReimpresion, TicketPayload, TicketJugada, imprimirTicket } from '../../../services/ticketService';
import { emitirVenta, onVenta } from '../../../utils/listasBus';
import { formatTimeCR } from '../../../utils/date';
import { formatTicketForPrint } from '../../../utils/ticketFormat';
import { TicketPreviewModal } from '../components/TicketPreviewModal';
import { fetchRiesgoNumero } from '../../analitica/services/analiticaService';
import { shareTicketAsImage, generateTicketImage } from '../../../utils/ticketImageShare';
import { payloadToTicketText, printTicketByDestination, isNativePrintSelected } from '../../../utils/printer';
import { shareImageNative } from '../../../utils/nativeShare';
import { isNativePlatform } from '../../../utils/platform';
import { useIsMobile } from '../../../hooks/useIsMobile';
import {
  finalizarVenta,
  huellaDeVenta,
  intentarIniciarVenta,
  limpiarIdempotencyKeyDeVenta,
  obtenerIdempotencyKeyDeVenta,
} from '../utils/ventaEnProceso';
import {
  obtenerSorteoMasProximoACerrar,
  obtenerSorteoMasProximoPorTipo,
  obtenerSorteosAbiertosDisponibles,
  tipoPosDesdeTipoId,
  type BackendSorteoSeleccionable,
} from '../utils/sorteoSelection';

// ============================================
// API CONFIG & HELPERS
// ============================================

interface BackendSorteo extends BackendSorteoSeleccionable {
  hora_apertura: string;
}

const CONCEPTOS_PAGO_POR_TIPO: Record<string, string[]> = {
  NORMAL: ['DIRECTO'],
  FECHAS: ['DIRECTO'],
  PARLEY: ['DIRECTO'],
  REVENTADO: ['DIRECTO', 'REVENTADO'],
  MEGA: ['DIRECTO', 'REVENTADO', 'MEGA_REVENTADO'],
  MONAZOS: ['EN_ORDEN'],
};

const LABEL_CONCEPTO_PAGO: Record<string, string> = {
  DIRECTO: 'Directo',
  REVENTADO: 'Reventado',
  MEGA_REVENTADO: 'Mega',
  EN_ORDEN: 'En Orden',
  DESORDEN: 'Desorden',
  PRIMEROS_2: 'Primeros 2',
  ULTIMOS_2: 'Ultimos 2',
};

const MONTO_MINIMO_JUGADA = 50;
const MENSAJE_MONTO_MINIMO_JUGADA = 'El monto mínimo por número es ₡50';
// Validación adicional: los montos solo se permiten en múltiplos de 5.
const MULTIPLO_MONTO_JUGADA = 5;
const MENSAJE_MONTO_MINIMO_TOAST = 'El monto mínimo permitido es ₡50';
const MENSAJE_MONTO_MULTIPLO_TOAST = 'Solo se permiten montos en múltiplos de ₡5';

// Map frontend tab type → backend tipo_id
function tipoIdFromSorteoType(st: SorteoType): string {
  return st === 'TICA' ? 'NORMAL' : st;
}

// Map TicketItemData → jugadas for backend { num1, num2?, monto }
function itemsToJugadas(items: TicketItemData[]): Array<{ num1: string; num2?: string; monto: number }> {
  const jugadas: Array<{ num1: string; num2?: string; monto: number }> = [];
  for (const item of items) {
    switch (item.modalidad) {
      case 'NORMAL':
        jugadas.push({ num1: item.numero, monto: item.monto });
        break;
      case 'REVENTADO':
        if (item.montoRev1 > 0) jugadas.push({ num1: item.numero, num2: 'REV1', monto: item.montoRev1 });
        if (item.montoRev2 > 0) jugadas.push({ num1: item.numero, num2: 'REV2', monto: item.montoRev2 });
        break;
      case 'MEGA_REVENTADO':
        if (item.rev1 > 0) jugadas.push({ num1: item.numero, num2: 'REV1', monto: item.rev1 });
        if (item.rev2 > 0) jugadas.push({ num1: item.numero, num2: 'REV2', monto: item.rev2 });
        break;
      case 'MONAZOS': {
        // P2 usa primeros 2 dígitos (d1+d2), U2 usa últimos 2 dígitos (d2+d3), ORDEN/DESORDEN usan 3 dígitos
        let num1: string;
        if (item.tipo === 'P2') {
          num1 = `${item.d1}${item.d2}`;  // Primeros 2: 970 → 97
        } else if (item.tipo === 'U2') {
          num1 = `${item.d2}${item.d3}`;  // Últimos 2: 970 → 70
        } else {
          num1 = `${item.d1}${item.d2}${item.d3}`;  // Orden/Desorden: 970
        }
        jugadas.push({ num1, num2: item.tipo, monto: item.monto });
        break;
      }
      case 'FECHAS':
        // Guardar fechas como dd-mm (número de mes) para que cuadre con la lista.
        {
          const monthMap: Record<string, number> = {
            'Enero': 1,
            'Febrero': 2,
            'Marzo': 3,
            'Abril': 4,
            'Mayo': 5,
            'Junio': 6,
            'Julio': 7,
            'Agosto': 8,
            'Septiembre': 9,
            'Octubre': 10,
            'Noviembre': 11,
            'Diciembre': 12,
          };
          const mm = (monthMap[item.mes] ?? 0).toString().padStart(2, '0');
          const dd = String(item.dia).padStart(2, '0');
          jugadas.push({ num1: `${dd}-${mm}`, monto: item.monto });
        }
        break;
      case 'PARLEY':
        jugadas.push({ num1: item.num1, num2: item.num2, monto: item.monto });
        break;
    }
  }
  return jugadas;
}

function tieneMontoMenorAlMinimo(item: TicketItemData): boolean {
  switch (item.modalidad) {
    case 'NORMAL':
    case 'MONAZOS':
    case 'FECHAS':
    case 'PARLEY':
      return item.monto < MONTO_MINIMO_JUGADA;
    case 'REVENTADO':
      return item.montoRev1 < MONTO_MINIMO_JUGADA ||
        (item.montoRev2 > 0 && item.montoRev2 < MONTO_MINIMO_JUGADA);
    case 'MEGA_REVENTADO':
      return item.rev1 < MONTO_MINIMO_JUGADA ||
        (item.rev2 > 0 && item.rev2 < MONTO_MINIMO_JUGADA);
  }
}

// Devuelve true si algún monto de la jugada NO es múltiplo de 5.
// (los campos que valen 0 son múltiplos válidos y no se penalizan)
function tieneMontoNoMultiploDe5(item: TicketItemData): boolean {
  const noEsMultiplo = (monto: number) => monto % MULTIPLO_MONTO_JUGADA !== 0;
  switch (item.modalidad) {
    case 'NORMAL':
    case 'MONAZOS':
    case 'FECHAS':
    case 'PARLEY':
      return noEsMultiplo(item.monto);
    case 'REVENTADO':
      return noEsMultiplo(item.montoRev1) ||
        (item.montoRev2 > 0 && noEsMultiplo(item.montoRev2));
    case 'MEGA_REVENTADO':
      return noEsMultiplo(item.rev1) ||
        (item.rev2 > 0 && noEsMultiplo(item.rev2));
  }
}

// Map jugadas backend → TicketItemData[] (inversa de itemsToJugadas)
// Usado para "jalar" tickets desde el backend cuando no están en localStorage
function jugadasToItems(
  jugadas: Array<{ num1: string; num2: string | null; monto: number }>,
  sorteoType: SorteoType,
): TicketItemData[] {
  const items: TicketItemData[] = [];

  if (sorteoType === 'REVENTADO' || sorteoType === 'MEGA') {
    // Agrupar REV1+REV2 consecutivos del mismo número
    let i = 0;
    while (i < jugadas.length) {
      const j = jugadas[i];
      let montoRev1 = 0;
      let montoRev2 = 0;
      const numero = j.num1;

      if (j.num2 === 'REV1') {
        montoRev1 = j.monto;
        // Si la siguiente jugada es REV2 del mismo número, agrupar
        if (i + 1 < jugadas.length && jugadas[i + 1].num1 === numero && jugadas[i + 1].num2 === 'REV2') {
          montoRev2 = jugadas[i + 1].monto;
          i += 2;
        } else {
          i++;
        }
      } else if (j.num2 === 'REV2') {
        montoRev2 = j.monto;
        i++;
      } else {
        // Jugada sin num2 en sorteo REVENTADO — tratar como REV1
        montoRev1 = j.monto;
        i++;
      }

      items.push({
        modalidad: 'REVENTADO',
        numero,
        montoRev1,
        montoRev2,
      });
    }
  } else if (sorteoType === 'MONAZOS') {
    for (const j of jugadas) {
      const tipo = (j.num2 || 'ORDEN') as MonazoTipo;
      const num = j.num1;
      let d1 = '', d2 = '', d3 = '';

      if (tipo === 'P2') {
        d1 = num[0] || '0';
        d2 = num[1] || '0';
        d3 = '0';
      } else if (tipo === 'U2') {
        d1 = '0';
        d2 = num[0] || '0';
        d3 = num[1] || '0';
      } else {
        // ORDEN / DESORDEN — 3 dígitos
        d1 = num[0] || '0';
        d2 = num[1] || '0';
        d3 = num[2] || '0';
      }

      items.push({ modalidad: 'MONAZOS', d1, d2, d3, monto: j.monto, tipo });
    }
  } else if (sorteoType === 'FECHAS') {
    for (const j of jugadas) {
      // num1 = "dd-mm"
      const parts = j.num1.split('-');
      const dia = parseInt(parts[0] || '1', 10);
      const mesIdx = parseInt(parts[1] || '1', 10) - 1;
      const mesNombre = MESES[mesIdx] || MESES[0];
      items.push({ modalidad: 'FECHAS', dia, mes: mesNombre, monto: j.monto });
    }
  } else if (sorteoType === 'PARLEY') {
    for (const j of jugadas) {
      items.push({ modalidad: 'PARLEY', num1: j.num1, num2: j.num2 || '00', monto: j.monto });
    }
  } else {
    // TICA / NORMAL
    for (const j of jugadas) {
      items.push({ modalidad: 'NORMAL', numero: j.num1, monto: j.monto });
    }
  }

  return items;
}

// ============================================
// SORTEO TABS COMPONENT
// ============================================
interface SorteoTabsProps {
  active: SorteoType;
  onChange: (sorteo: SorteoType) => void;
  ticketCounts: Record<SorteoType, number>;
}

const SorteoTabs: React.FC<SorteoTabsProps> = ({ active, onChange, ticketCounts }) => (
  <div className="flex flex-wrap gap-1 p-2 bg-gray-800 rounded-t-lg">
    {SORTEO_CONFIG.map(s => (
      <button
        key={s.id}
        onClick={() => onChange(s.id)}
        className={`
          px-3 py-2.5 text-sm font-bold rounded transition-all min-h-[44px]
          ${active === s.id
            ? `${s.color} text-white`
            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }
        `}
      >
        {s.label}
        {ticketCounts[s.id] > 0 && (
          <span className="ml-1 px-1.5 py-0.5 text-xs bg-white/20 rounded">
            {ticketCounts[s.id]}
          </span>
        )}
      </button>
    ))}
  </div>
);

// ============================================
// SORTEO COUNTDOWN COMPONENT
// ============================================
interface SorteoCountdownProps {
  horaCierre: string; // "HH:MM:SS" or "HH:MM"
  fecha: string;      // "YYYY-MM-DD"
  estado?: string;    // "ABIERTO" or "CERRADO"
  onExpired?: () => void;
}

const SorteoCountdown: React.FC<SorteoCountdownProps> = ({ horaCierre, fecha, estado = 'ABIERTO', onExpired }) => {
  const [remaining, setRemaining] = useState('');
  const [urgency, setUrgency] = useState<'normal' | 'warning' | 'critical'>('normal');
  const expiredFiredRef = useRef(false);

  useEffect(() => {
    expiredFiredRef.current = false;

    if (estado !== 'ABIERTO') return;

    const computeRemaining = () => {
      const [h, m, s] = horaCierre.split(':').map(Number);
      // Usar fecha de HOY para el cierre (sorteos recurrentes tienen fecha vieja)
      const now = new Date();
      const closeDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), h, m, s || 0, 0);
      const diffMs = closeDate.getTime() - now.getTime();

      if (diffMs <= 0) {
        setRemaining('CERRADO');
        setUrgency('critical');
        if (!expiredFiredRef.current) {
          expiredFiredRef.current = true;
          onExpired?.();
        }
        return;
      }

      const totalSec = Math.floor(diffMs / 1000);
      const hrs = Math.floor(totalSec / 3600);
      const mins = Math.floor((totalSec % 3600) / 60);
      const secs = totalSec % 60;

      const pad = (n: number) => n.toString().padStart(2, '0');
      const timeStr = hrs > 0
        ? `${pad(hrs)}:${pad(mins)}:${pad(secs)}`
        : `${pad(mins)}:${pad(secs)}`;
      setRemaining(`CIERRA EN ${timeStr}`);

      if (totalSec <= 60) setUrgency('critical');         // <= 1 min → rojo parpadeante
      else if (totalSec <= 300) setUrgency('warning');    // <= 5 min → amarillo
      else setUrgency('normal');                          // > 5 min → verde
    };

    computeRemaining();
    const timer = setInterval(computeRemaining, 1000);
    return () => clearInterval(timer);
  }, [horaCierre, fecha, estado, onExpired]);

  // Si backend dice CERRADO, mostrar badge
  if (estado !== 'ABIERTO' || remaining === 'CERRADO') {
    return (
      <div className="ml-auto flex items-center gap-1.5 font-mono text-sm font-bold text-red-400 animate-pulse">
        <div className="w-2 h-2 bg-red-400 rounded-full"></div>
        <span>CERRADO</span>
      </div>
    );
  }

  const colorClasses = {
    normal: 'text-emerald-400',
    warning: 'text-yellow-400',
    critical: 'text-red-400 animate-pulse',
  };

  return (
    <div className={`ml-auto flex items-center gap-1.5 font-mono text-sm font-bold ${colorClasses[urgency]}`}>
      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
      </svg>
      <span>{remaining}</span>
    </div>
  );
};

// ============================================
// CAPTURE FORM COMPONENT (por modalidad)
// ============================================
interface CaptureFormProps {
  sorteo: SorteoType;
  onAdd: (item: TicketItemData) => void;
  editingItem?: TicketItem | null;
  onCancelEdit?: () => void;
  onSaveEdit?: (item: TicketItemData) => void;
  resetAmountKey?: number;
  disabled?: boolean;
}

// Tipos de entrada para TICA
type TicaInputMode = 'SIMPLE' | 'RANGO' | 'INICIA' | 'TERMINA' | 'PARES';

const CaptureForm: React.FC<CaptureFormProps> = ({
  sorteo,
  onAdd,
  editingItem,
  onCancelEdit,
  onSaveEdit,
  resetAmountKey,
  disabled,
}) => {
  const modalidad = getSorteoModalidad(sorteo);
  const firstRef = useRef<HTMLInputElement>(null);
  const montoRef = useRef<HTMLInputElement>(null);
  const fechasMesRef = useRef<HTMLInputElement>(null);
  const parleyNum2Ref = useRef<HTMLInputElement>(null);
  const rangoHastaRef = useRef<HTMLInputElement>(null);
  const rev1Ref = useRef<HTMLInputElement>(null);
  const rev2Ref = useRef<HTMLInputElement>(null);
  const num2Ref = useRef<HTMLInputElement>(null);

  // Form states
  const [numero, setNumero] = useState('');
  const [monto, setMonto] = useState('0');
  const [rev1, setRev1] = useState('');
  const [rev2, setRev2] = useState('');
  const [num2, setNum2] = useState('');
  const [d1, setD1] = useState('');
  const [d2, setD2] = useState('');
  const [d3, setD3] = useState('');
  const [dia, setDia] = useState('');
  const [mes, setMes] = useState('');
  const [mesNum, setMesNum] = useState('');
  const [num1, setNum1] = useState('');

  // MONAZOS selector
  const [monazoTipos, setMonazoTipos] = useState<MonazoTipo[]>(['ORDEN']);

  // TICA specific states
  const [ticaMode, setTicaMode] = useState<TicaInputMode>('SIMPLE');
  const [rangoDesde, setRangoDesde] = useState('');
  const [rangoHasta, setRangoHasta] = useState('');
  const [digitoFiltro, setDigitoFiltro] = useState('');

  // Reset form
  const resetForm = useCallback((preserveMonto = false, preserveReventado = false) => {
    setNumero('');
    if (!preserveMonto) {
      setMonto('0');
    }
    if (!preserveReventado) {
      setRev1('');
      setRev2('');
    }
    setD1('');
    setD2('');
    setD3('');
    setDia('');
    setMes('');
    setMesNum('');
    setNum1('');
    setMonazoTipos(['ORDEN']);
    setRangoDesde('');
    setRangoHasta('');
    setDigitoFiltro('');
    setTimeout(() => firstRef.current?.focus(), 10);
  }, []);

  // Load editing data
  useEffect(() => {
    if (editingItem) {
      const data = editingItem.data;
      setTicaMode('SIMPLE'); // Always load as simple when editing
      switch (data.modalidad) {
        case 'NORMAL':
          setNumero(data.numero);
          setMonto(data.monto.toString());
          break;
        case 'REVENTADO':
          setNumero(data.numero);
          setRev1(data.montoRev1.toString());
          setRev2(data.montoRev2.toString());
          break;
        case 'MEGA_REVENTADO':
          setNumero(data.numero);
          setRev1(data.rev1.toString());
          setRev2(data.rev2.toString());
          break;
        case 'MONAZOS':
          setD1(data.d1);
          setD2(data.d2);
          setD3(data.d3);
          setMonto(data.monto.toString());
          setMonazoTipos([data.tipo]);
          break;
        case 'FECHAS':
          setDia(data.dia.toString());
          setMes(data.mes);
          {
            const idx = MESES.findIndex(m => m === data.mes);
            setMesNum(idx >= 0 ? String(idx + 1) : '');
          }
          setMonto(data.monto.toString());
          break;
        case 'PARLEY':
          setNum1(data.num1);
          setNumero(data.num2);
          setMonto(data.monto.toString());
          break;
      }
      setTimeout(() => firstRef.current?.focus(), 10);
    }
  }, [editingItem]);

  // Reset on sorteo change
  useEffect(() => {
    if (!editingItem) {
      resetForm(false);
      setTicaMode('SIMPLE');
    }
  }, [sorteo, editingItem, resetForm]);

  // MEGA UX: mostrar Rev2 como 0 por defecto
  useEffect(() => {
    if (editingItem) return;
    if (modalidad !== 'MEGA_REVENTADO') return;
    if (rev2 === '') setRev2('0');
  }, [modalidad, editingItem, rev2]);

  // Auto focus
  useEffect(() => {
    setTimeout(() => firstRef.current?.focus(), 50);
  }, [sorteo, ticaMode]);

  useEffect(() => {
    setMonto('0');
  }, [resetAmountKey]);

  // Generate numbers for TICA modes
  const generateTicaNumbers = (): string[] => {
    switch (ticaMode) {
      case 'SIMPLE':
        return numero ? [formatNumber2(numero)] : [];
      
      case 'RANGO': {
        if (!rangoDesde || !rangoHasta) return [];
        const desde = parseInt(rangoDesde);
        const hasta = parseInt(rangoHasta);
        if (isNaN(desde) || isNaN(hasta) || desde > hasta || desde < 0 || hasta > 99) return [];
        const nums: string[] = [];
        for (let i = desde; i <= hasta; i++) {
          nums.push(formatNumber2(i));
        }
        return nums;
      }
      
      case 'INICIA': {
        if (!digitoFiltro || digitoFiltro.length !== 1) return [];
        const d = parseInt(digitoFiltro);
        if (isNaN(d) || d < 0 || d > 9) return [];
        const nums: string[] = [];
        for (let i = 0; i <= 9; i++) {
          nums.push(formatNumber2(d * 10 + i)); // 90, 91, 92...99
        }
        return nums;
      }
      
      case 'TERMINA': {
        if (!digitoFiltro || digitoFiltro.length !== 1) return [];
        const d = parseInt(digitoFiltro);
        if (isNaN(d) || d < 0 || d > 9) return [];
        const nums: string[] = [];
        for (let i = 0; i <= 9; i++) {
          nums.push(formatNumber2(i * 10 + d)); // 03, 13, 23...93
        }
        return nums;
      }
      
      case 'PARES': {
        // 00, 11, 22, 33, 44, 55, 66, 77, 88, 99
        return ['00', '11', '22', '33', '44', '55', '66', '77', '88', '99'];
      }
      
      default:
        return [];
    }
  };

  // Handle submit
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (disabled) return;

    const requirePositive = (value: string, focusRef?: React.RefObject<HTMLInputElement | null>) => {
      const n = parseInt(value, 10) || 0;
      if (n <= 0) {
        const el = focusRef?.current;
        if (el) {
          setTimeout(() => {
            el.focus();
            try { el.select(); } catch {}
          }, 0);
        }
        return null;
      }
      return n;
    };

    // Handle TICA with multiple numbers
    if (modalidad === 'NORMAL' && !editingItem) {
      const numbers = generateTicaNumbers();
      if (numbers.length === 0) return;

      const montoVal = requirePositive(monto, montoRef);
      if (montoVal === null) return;

      numbers.forEach(num => {
        onAdd({ modalidad: 'NORMAL', numero: num, monto: montoVal });
      });
      resetForm(true);
      return;
    }

    // Handle TICA with multiple numbers (REVENTADO)
    if (modalidad === 'REVENTADO' && !editingItem) {
      const numbers = generateTicaNumbers();
      if (numbers.length === 0) return;

      const rev1Val = requirePositive(rev1, rev1Ref);
      if (rev1Val === null) return;
      // En REVENTADO, Rev2 puede ser 0 (opcional). Si está vacío, se toma como 0.
      let rev2Val = Math.max(0, parseInt(rev2 || '0', 10) || 0);
      // Rev2 no puede ser mayor a Rev1
      if (rev2Val > rev1Val) rev2Val = rev1Val;

      numbers.forEach(num => {
        onAdd({ modalidad: 'REVENTADO', numero: num, montoRev1: rev1Val, montoRev2: rev2Val });
      });
      // Mantener Rev1/Rev2 para capturas rápidas
      resetForm(true, true);
      return;
    }

    // Handle TICA with multiple numbers (MEGA)
    if (modalidad === 'MEGA_REVENTADO' && !editingItem) {
      const numbers = generateTicaNumbers();
      if (numbers.length === 0) return;

      const rev1Val = requirePositive(rev1, rev1Ref);
      if (rev1Val === null) return;
      let rev2Val = Math.max(0, parseInt(rev2 || '0', 10) || 0);
      // Rev2 no puede ser mayor a Rev1
      if (rev2Val > rev1Val) rev2Val = rev1Val;

      numbers.forEach(num => {
        onAdd({ modalidad: 'MEGA_REVENTADO', numero: num, rev1: rev1Val, rev2: rev2Val });
      });

      // Mantener Rev1/Rev2 para capturas rápidas
      resetForm(true, true);
      return;
    }

    let item: TicketItemData | null = null;

    switch (modalidad) {
      case 'NORMAL':
        if (!numero) return;
        {
          const montoVal = requirePositive(monto, montoRef);
          if (montoVal === null) return;
          item = { modalidad: 'NORMAL', numero: formatNumber2(numero), monto: montoVal };
        }
        break;
      case 'REVENTADO':
        if (!numero) return;
        {
          const rev1Val = requirePositive(rev1, rev1Ref);
          if (rev1Val === null) return;
          // En REVENTADO, Rev2 puede ser 0 (opcional). Si está vacío, se toma como 0.
          let rev2Val = Math.max(0, parseInt(rev2 || '0', 10) || 0);
          // Rev2 no puede ser mayor a Rev1
          if (rev2Val > rev1Val) rev2Val = rev1Val;
          item = { modalidad: 'REVENTADO', numero: formatNumber2(numero), montoRev1: rev1Val, montoRev2: rev2Val };
        }
        break;
      case 'MEGA_REVENTADO':
        if (!numero) return;
        {
          const rev1Val = requirePositive(rev1, rev1Ref);
          if (rev1Val === null) return;
          let rev2Val = Math.max(0, parseInt(rev2 || '0', 10) || 0);
          // Rev2 no puede ser mayor a Rev1
          if (rev2Val > rev1Val) rev2Val = rev1Val;
          item = { modalidad: 'MEGA_REVENTADO', numero: formatNumber2(numero), rev1: rev1Val, rev2: rev2Val };
        }
        break;
      case 'MONAZOS':
        // Solo ORDEN activo; siempre requiere los 3 dígitos
        if (!d1 || !d2 || !d3) return;
        {
          const montoVal = requirePositive(monto, montoRef);
          if (montoVal === null) return;
          const tipos = (editingItem ? [monazoTipos[0] ?? 'ORDEN'] : (monazoTipos.length ? monazoTipos : ['ORDEN'])) as MonazoTipo[];
          if (!editingItem) {
            tipos.forEach(tipo => {
              onAdd({ modalidad: 'MONAZOS', d1, d2, d3, monto: montoVal, tipo });
            });
            resetForm(true);
            return;
          }
          item = { modalidad: 'MONAZOS', d1, d2, d3, monto: montoVal, tipo: tipos[0] };
        }
        break;
      case 'FECHAS':
        if (!dia) return;
        {
          if (!mesNum || !mes) {
            setTimeout(() => fechasMesRef.current?.focus(), 0);
            return;
          }
          const montoVal = requirePositive(monto, montoRef);
          if (montoVal === null) return;
          item = { modalidad: 'FECHAS', dia: parseInt(dia) || 1, mes, monto: montoVal };
        }
        break;
      case 'PARLEY':
        if (!num1 || !numero) return;
        {
          const montoVal = requirePositive(monto, montoRef);
          if (montoVal === null) return;
          item = { modalidad: 'PARLEY', num1: formatNumber2(num1), num2: formatNumber2(numero), monto: montoVal };
        }
        break;
    }

    if (item) {
      if (editingItem && onSaveEdit) {
        onSaveEdit(item);
        resetForm(false);
      } else {
        onAdd(item);
        // FECHAS: mantener monto (y mes) para capturas rápidas
        if (modalidad === 'FECHAS') {
          setDia('');
          setMes('');
          setMesNum('');
          setTimeout(() => firstRef.current?.focus(), 10);
          return;
        }
        resetForm(true, modalidad === 'REVENTADO');
      }
    }
  };

  // Input styles
  const inputCls = "w-full px-3 py-3 text-xl font-mono font-bold text-center border-2 border-gray-600 bg-gray-900 text-white rounded focus:border-yellow-400 focus:outline-none";
  const labelCls = "block text-xs text-gray-400 mb-1 uppercase tracking-wide";
  const smallInputCls = "w-16 px-2 py-3 text-xl font-mono font-bold text-center border-2 border-gray-600 bg-gray-900 text-white rounded focus:border-yellow-400 focus:outline-none";
  const tabCls = (active: boolean) => `flex-1 py-1 px-1 text-[10px] font-bold rounded transition-all ${active ? 'bg-yellow-500 text-black' : 'bg-gray-700 text-gray-400 hover:bg-gray-600'}`;

  const megaSecondEnabled = false; // segundo número eliminado: MEGA usa solo Rev1/Rev2

  // Focus next on maxLength
  const handleNumeroChange = (val: string, maxLen: number, nextRef?: React.RefObject<HTMLInputElement | null>) => {
    const clean = val.replace(/\D/g, '').slice(0, maxLen);
    if (clean.length === maxLen && nextRef?.current) {
      setTimeout(() => nextRef.current?.focus(), 10);
    }
    return clean;
  };

  // Preview numbers for TICA
  const previewNumbers = (modalidad === 'NORMAL' || modalidad === 'REVENTADO' || modalidad === 'MEGA_REVENTADO') && !editingItem
    ? generateTicaNumbers()
    : [];

  // Render form by modalidad
  const renderForm = () => {
    switch (modalidad) {
      case 'NORMAL':
        return (
          <>
            {/* Mode selector - only when not editing */}
            {!editingItem && (
              <div className="flex gap-1 mb-3">
                <button type="button" onClick={() => setTicaMode('SIMPLE')} className={tabCls(ticaMode === 'SIMPLE')}>Simple</button>
                <button type="button" onClick={() => setTicaMode('RANGO')} className={tabCls(ticaMode === 'RANGO')}>Rango</button>
                <button type="button" onClick={() => setTicaMode('INICIA')} className={tabCls(ticaMode === 'INICIA')}>Inicia</button>
                <button type="button" onClick={() => setTicaMode('TERMINA')} className={tabCls(ticaMode === 'TERMINA')}>Term.</button>
                <button type="button" onClick={() => setTicaMode('PARES')} className={tabCls(ticaMode === 'PARES')}>Pares</button>
              </div>
            )}

            {/* Simple mode */}
            {(ticaMode === 'SIMPLE' || editingItem) && (
              <div>
                <label className={labelCls}>Número</label>
                <input
                  ref={firstRef}
                  type="text"
                  inputMode="numeric"
                  value={numero}
                  onChange={e => setNumero(handleNumeroChange(e.target.value, 2, montoRef))}
                  placeholder="00"
                  className={inputCls}
                  autoComplete="off"
                />
              </div>
            )}

            {/* Range mode */}
            {ticaMode === 'RANGO' && !editingItem && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className={labelCls}>Desde</label>
                  <input
                    ref={firstRef}
                    type="text"
                    inputMode="numeric"
                    value={rangoDesde}
                    onChange={e => setRangoDesde(handleNumeroChange(e.target.value, 2, rangoHastaRef))}
                    placeholder="00"
                    className={inputCls}
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className={labelCls}>Hasta</label>
                  <input
                    ref={rangoHastaRef}
                    type="text"
                    inputMode="numeric"
                    value={rangoHasta}
                    onChange={e => setRangoHasta(handleNumeroChange(e.target.value, 2, montoRef))}
                    placeholder="99"
                    className={inputCls}
                    autoComplete="off"
                  />
                </div>
              </div>
            )}

            {/* Inicia con mode */}
            {ticaMode === 'INICIA' && !editingItem && (
              <div>
                <label className={labelCls}>Inicia con (0-9)</label>
                <input
                  ref={firstRef}
                  type="text"
                  inputMode="numeric"
                  value={digitoFiltro}
                  onChange={e => {
                    const v = e.target.value.replace(/\D/g, '').slice(0, 1);
                    setDigitoFiltro(v);
                    if (v) setTimeout(() => montoRef.current?.focus(), 10);
                  }}
                  placeholder="9"
                  className={inputCls}
                  autoComplete="off"
                />
                <p className="text-xs text-gray-500 mt-1 text-center">
                  {digitoFiltro ? `→ ${digitoFiltro}0, ${digitoFiltro}1, ${digitoFiltro}2...${digitoFiltro}9` : 'Ej: 9 → 90,91,92...99'}
                </p>
              </div>
            )}

            {/* Termina con mode */}
            {ticaMode === 'TERMINA' && !editingItem && (
              <div>
                <label className={labelCls}>Termina con (0-9)</label>
                <input
                  ref={firstRef}
                  type="text"
                  inputMode="numeric"
                  value={digitoFiltro}
                  onChange={e => {
                    const v = e.target.value.replace(/\D/g, '').slice(0, 1);
                    setDigitoFiltro(v);
                    if (v) setTimeout(() => montoRef.current?.focus(), 10);
                  }}
                  placeholder="3"
                  className={inputCls}
                  autoComplete="off"
                />
                <p className="text-xs text-gray-500 mt-1 text-center">
                  {digitoFiltro ? `→ 0${digitoFiltro}, 1${digitoFiltro}, 2${digitoFiltro}...9${digitoFiltro}` : 'Ej: 3 → 03,13,23...93'}
                </p>
              </div>
            )}

            {/* Pares mode - no input needed, just shows preview */}
            {ticaMode === 'PARES' && !editingItem && (
              <div className="bg-gray-800 rounded p-2">
                <p className="text-xs text-gray-400 mb-1">10 números (pares):</p>
                <p className="text-xs text-yellow-400 font-mono">
                  00, 11, 22, 33, 44, 55, 66, 77, 88, 99
                </p>
              </div>
            )}

            {/* Preview of numbers to add */}
            {previewNumbers.length > 1 && ticaMode !== 'PARES' && (
              <div className="bg-gray-800 rounded p-2 mt-2">
                <p className="text-xs text-gray-400 mb-1">{previewNumbers.length} números:</p>
                <p className="text-xs text-yellow-400 font-mono break-all">
                  {previewNumbers.join(', ')}
                </p>
              </div>
            )}

            <div>
              <label className={labelCls}>Monto ₡</label>
              <input
                ref={montoRef}
                type="text"
                inputMode="numeric"
                value={monto}
                onChange={e => setMonto(e.target.value.replace(/\D/g, ''))}
                onFocus={e => e.target.select()}
                placeholder="100"
                className={inputCls}
                autoComplete="off"
              />
            </div>
          </>
        );

      case 'REVENTADO':
        return (
          <>
            {/* Mode selector - only when not editing */}
            {!editingItem && (
              <div className="flex gap-1 mb-3">
                <button type="button" onClick={() => setTicaMode('SIMPLE')} className={tabCls(ticaMode === 'SIMPLE')}>Simple</button>
                <button type="button" onClick={() => setTicaMode('RANGO')} className={tabCls(ticaMode === 'RANGO')}>Rango</button>
                <button type="button" onClick={() => setTicaMode('INICIA')} className={tabCls(ticaMode === 'INICIA')}>Inicia</button>
                <button type="button" onClick={() => setTicaMode('TERMINA')} className={tabCls(ticaMode === 'TERMINA')}>Term.</button>
                <button type="button" onClick={() => setTicaMode('PARES')} className={tabCls(ticaMode === 'PARES')}>Pares</button>
              </div>
            )}

            {/* Simple mode */}
            {(ticaMode === 'SIMPLE' || editingItem) && (
              <div>
                <label className={labelCls}>Número</label>
                <input
                  ref={firstRef}
                  type="text"
                  inputMode="numeric"
                  value={numero}
                  onChange={e => setNumero(handleNumeroChange(e.target.value, 2, rev1Ref))}
                  placeholder="00"
                  className={inputCls}
                  autoComplete="off"
                />
              </div>
            )}

            {/* Range mode */}
            {ticaMode === 'RANGO' && !editingItem && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className={labelCls}>Desde</label>
                  <input
                    ref={firstRef}
                    type="text"
                    inputMode="numeric"
                    value={rangoDesde}
                    onChange={e => setRangoDesde(handleNumeroChange(e.target.value, 2, rangoHastaRef))}
                    placeholder="00"
                    className={inputCls}
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className={labelCls}>Hasta</label>
                  <input
                    ref={rangoHastaRef}
                    type="text"
                    inputMode="numeric"
                    value={rangoHasta}
                    onChange={e => setRangoHasta(handleNumeroChange(e.target.value, 2, rev1Ref))}
                    placeholder="99"
                    className={inputCls}
                    autoComplete="off"
                  />
                </div>
              </div>
            )}

            {/* Inicia con mode */}
            {ticaMode === 'INICIA' && !editingItem && (
              <div>
                <label className={labelCls}>Inicia con (0-9)</label>
                <input
                  ref={firstRef}
                  type="text"
                  inputMode="numeric"
                  value={digitoFiltro}
                  onChange={e => {
                    const v = e.target.value.replace(/\D/g, '').slice(0, 1);
                    setDigitoFiltro(v);
                    if (v) setTimeout(() => rev1Ref.current?.focus(), 10);
                  }}
                  placeholder="9"
                  className={inputCls}
                  autoComplete="off"
                />
                <p className="text-xs text-gray-500 mt-1 text-center">
                  {digitoFiltro ? `→ ${digitoFiltro}0, ${digitoFiltro}1, ${digitoFiltro}2...${digitoFiltro}9` : 'Ej: 9 → 90,91,92...99'}
                </p>
              </div>
            )}

            {/* Termina con mode */}
            {ticaMode === 'TERMINA' && !editingItem && (
              <div>
                <label className={labelCls}>Termina con (0-9)</label>
                <input
                  ref={firstRef}
                  type="text"
                  inputMode="numeric"
                  value={digitoFiltro}
                  onChange={e => {
                    const v = e.target.value.replace(/\D/g, '').slice(0, 1);
                    setDigitoFiltro(v);
                    if (v) setTimeout(() => rev1Ref.current?.focus(), 10);
                  }}
                  placeholder="3"
                  className={inputCls}
                  autoComplete="off"
                />
                <p className="text-xs text-gray-500 mt-1 text-center">
                  {digitoFiltro ? `→ 0${digitoFiltro}, 1${digitoFiltro}, 2${digitoFiltro}...9${digitoFiltro}` : 'Ej: 3 → 03,13,23...93'}
                </p>
              </div>
            )}

            {/* Pares mode - no input needed, just shows preview */}
            {ticaMode === 'PARES' && !editingItem && (
              <div className="bg-gray-800 rounded p-2">
                <p className="text-xs text-gray-400 mb-1">10 números (pares):</p>
                <p className="text-xs text-yellow-400 font-mono">
                  00, 11, 22, 33, 44, 55, 66, 77, 88, 99
                </p>
              </div>
            )}

            {/* Preview of numbers to add */}
            {previewNumbers.length > 1 && ticaMode !== 'PARES' && (
              <div className="bg-gray-800 rounded p-2 mt-2">
                <p className="text-xs text-gray-400 mb-1">{previewNumbers.length} números:</p>
                <p className="text-xs text-yellow-400 font-mono break-all">
                  {previewNumbers.join(', ')}
                </p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={labelCls}>Rev1 ₡</label>
                <input
                  ref={rev1Ref}
                  type="text"
                  inputMode="numeric"
                  value={rev1}
                  onChange={e => setRev1(e.target.value.replace(/\D/g, ''))}
                  onFocus={e => e.target.select()}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      montoRef.current?.focus();
                    }
                  }}
                  placeholder="100"
                  className={inputCls}
                  autoComplete="off"
                />
              </div>
              <div>
                <label className={labelCls}>REV2 ₡</label>
                <input
                  ref={montoRef}
                  type="text"
                  inputMode="numeric"
                  value={rev2}
                  onChange={e => {
                    const val = e.target.value.replace(/\D/g, '');
                    const rev1Val = parseInt(rev1 || '0', 10) || 0;
                    const rev2Val = parseInt(val || '0', 10) || 0;
                    if (rev1Val > 0 && rev2Val > rev1Val) {
                      setRev2(rev1Val.toString());
                    } else {
                      setRev2(val);
                    }
                  }}
                  onFocus={e => e.target.select()}
                  placeholder="100"
                  disabled={!(parseInt(rev1 || '0', 10) || 0)}
                  className={`${inputCls} ${(() => { const r1 = parseInt(rev1||'0',10)||0; const r2 = parseInt(rev2||'0',10)||0; return r2 > 0 && r1 > 0 && r2 > r1 ? 'ring-2 ring-red-500' : ''; })() || !(parseInt(rev1||'0',10)||0) ? 'opacity-50 cursor-not-allowed' : ''}`}
                  autoComplete="off"
                />
              </div>
            </div>
          </>
        );

      case 'MEGA_REVENTADO':
        return (
          <>
            {/* Mode selector - only when not editing */}
            {!editingItem && (
              <div className="flex gap-1 mb-3">
                <button type="button" onClick={() => setTicaMode('SIMPLE')} className={tabCls(ticaMode === 'SIMPLE')}>Simple</button>
                <button type="button" onClick={() => setTicaMode('RANGO')} className={tabCls(ticaMode === 'RANGO')}>Rango</button>
                <button type="button" onClick={() => setTicaMode('INICIA')} className={tabCls(ticaMode === 'INICIA')}>Inicia</button>
                <button type="button" onClick={() => setTicaMode('TERMINA')} className={tabCls(ticaMode === 'TERMINA')}>Term.</button>
                <button type="button" onClick={() => setTicaMode('PARES')} className={tabCls(ticaMode === 'PARES')}>Pares</button>
              </div>
            )}

            {/* Simple mode */}
            {(ticaMode === 'SIMPLE' || editingItem) && (
              <div>
                <label className={labelCls}>Número</label>
                <input
                  ref={firstRef}
                  type="text"
                  inputMode="numeric"
                  value={numero}
                  onChange={e => setNumero(handleNumeroChange(e.target.value, 2, rev1Ref))}
                  placeholder="00"
                  className={inputCls}
                  autoComplete="off"
                />
              </div>
            )}

            {/* Range mode */}
            {ticaMode === 'RANGO' && !editingItem && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className={labelCls}>Desde</label>
                  <input
                    ref={firstRef}
                    type="text"
                    inputMode="numeric"
                    value={rangoDesde}
                    onChange={e => setRangoDesde(handleNumeroChange(e.target.value, 2, rangoHastaRef))}
                    placeholder="00"
                    className={inputCls}
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className={labelCls}>Hasta</label>
                  <input
                    ref={rangoHastaRef}
                    type="text"
                    inputMode="numeric"
                    value={rangoHasta}
                    onChange={e => setRangoHasta(handleNumeroChange(e.target.value, 2, rev1Ref))}
                    placeholder="99"
                    className={inputCls}
                    autoComplete="off"
                  />
                </div>
              </div>
            )}

            {/* Inicia con mode */}
            {ticaMode === 'INICIA' && !editingItem && (
              <div>
                <label className={labelCls}>Inicia con (0-9)</label>
                <input
                  ref={firstRef}
                  type="text"
                  inputMode="numeric"
                  value={digitoFiltro}
                  onChange={e => {
                    const v = e.target.value.replace(/\D/g, '').slice(0, 1);
                    setDigitoFiltro(v);
                    if (v) setTimeout(() => rev1Ref.current?.focus(), 10);
                  }}
                  placeholder="9"
                  className={inputCls}
                  autoComplete="off"
                />
                <p className="text-xs text-gray-500 mt-1 text-center">
                  {digitoFiltro ? `→ ${digitoFiltro}0, ${digitoFiltro}1, ${digitoFiltro}2...${digitoFiltro}9` : 'Ej: 9 → 90,91,92...99'}
                </p>
              </div>
            )}

            {/* Termina con mode */}
            {ticaMode === 'TERMINA' && !editingItem && (
              <div>
                <label className={labelCls}>Termina con (0-9)</label>
                <input
                  ref={firstRef}
                  type="text"
                  inputMode="numeric"
                  value={digitoFiltro}
                  onChange={e => {
                    const v = e.target.value.replace(/\D/g, '').slice(0, 1);
                    setDigitoFiltro(v);
                    if (v) setTimeout(() => rev1Ref.current?.focus(), 10);
                  }}
                  placeholder="3"
                  className={inputCls}
                  autoComplete="off"
                />
                <p className="text-xs text-gray-500 mt-1 text-center">
                  {digitoFiltro ? `→ 0${digitoFiltro}, 1${digitoFiltro}, 2${digitoFiltro}...9${digitoFiltro}` : 'Ej: 3 → 03,13,23...93'}
                </p>
              </div>
            )}

            {/* Pares mode - no input needed, just shows preview */}
            {ticaMode === 'PARES' && !editingItem && (
              <div className="bg-gray-800 rounded p-2">
                <p className="text-xs text-gray-400 mb-1">10 números (pares):</p>
                <p className="text-xs text-yellow-400 font-mono">
                  00, 11, 22, 33, 44, 55, 66, 77, 88, 99
                </p>
              </div>
            )}

            {/* Preview of numbers to add */}
            {previewNumbers.length > 1 && ticaMode !== 'PARES' && (
              <div className="bg-gray-800 rounded p-2 mt-2">
                <p className="text-xs text-gray-400 mb-1">{previewNumbers.length} números:</p>
                <p className="text-xs text-yellow-400 font-mono break-all">
                  {previewNumbers.join(', ')}
                </p>
              </div>
            )}

            {/* Rev1 → Rev2 */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={labelCls}>Rev1 ₡</label>
                <input
                  ref={rev1Ref}
                  type="text"
                  inputMode="numeric"
                  value={rev1}
                  onChange={e => setRev1(e.target.value.replace(/\D/g, ''))}
                  onFocus={e => e.target.select()}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      rev2Ref.current?.focus();
                    }
                  }}
                  placeholder="100"
                  className={inputCls}
                  autoComplete="off"
                />
              </div>
              <div>
                <label className={labelCls}>Rev2 ₡</label>
                <input
                  ref={rev2Ref}
                  type="text"
                  inputMode="numeric"
                  value={rev2}
                  onChange={e => {
                    const val = e.target.value.replace(/\D/g, '');
                    const rev1Val = parseInt(rev1 || '0', 10) || 0;
                    const rev2Val = parseInt(val || '0', 10) || 0;
                    if (rev1Val > 0 && rev2Val > rev1Val) {
                      setRev2(rev1Val.toString());
                    } else {
                      setRev2(val);
                    }
                  }}
                  onFocus={e => e.target.select()}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      e.currentTarget.form?.requestSubmit();
                    }
                  }}
                  placeholder="100"
                  disabled={!(parseInt(rev1 || '0', 10) || 0)}
                  className={`${inputCls} ${!(parseInt(rev1 || '0', 10) || 0) ? 'opacity-50 cursor-not-allowed' : ''}`}
                  autoComplete="off"
                />
              </div>
            </div>
          </>
        );

      case 'MONAZOS':
        return (
          <>
            {/* Selector de subtipo oculto: solo ORDEN está activo */}
            <div>
              <label className={labelCls}>Dígitos</label>
              <div className="flex items-center justify-center gap-2">
                <input
                  ref={firstRef}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={d1}
                  onChange={e => {
                    const v = e.target.value.replace(/\D/g, '').slice(0, 1);
                    setD1(v);
                    if (v) document.getElementById('d2-input')?.focus();
                  }}
                  placeholder="0"
                  className={smallInputCls}
                  autoComplete="off"
                />
                <span className="text-2xl text-gray-500">-</span>
                <input
                  id="d2-input"
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={d2}
                  onChange={e => {
                    const v = e.target.value.replace(/\D/g, '').slice(0, 1);
                    setD2(v);
                    if (v) {
                      // ORDEN siempre requiere d3
                      document.getElementById('d3-input')?.focus();
                    }
                  }}
                  placeholder="0"
                  className={smallInputCls}
                  autoComplete="off"
                />
{/* d3 siempre visible — solo ORDEN activo (3 dígitos) */}
                <span className="text-2xl text-gray-500">-</span>
                <input
                  id="d3-input"
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={d3}
                  onChange={e => {
                    const v = e.target.value.replace(/\D/g, '').slice(0, 1);
                    setD3(v);
                    if (v) montoRef.current?.focus();
                  }}
                  placeholder="0"
                  className={smallInputCls}
                  autoComplete="off"
                />
              </div>
            </div>
            <div>
              <label className={labelCls}>Monto ₡</label>
              <input
                ref={montoRef}
                type="text"
                inputMode="numeric"
                value={monto}
                onChange={e => setMonto(e.target.value.replace(/\D/g, ''))}
                onFocus={e => e.target.select()}
                placeholder="100"
                className={inputCls}
                autoComplete="off"
              />
            </div>
          </>
        );

      case 'FECHAS':
        return (
          <>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={labelCls}>Día</label>
                <input
                  ref={firstRef}
                  type="text"
                  inputMode="numeric"
                  maxLength={2}
                  value={dia}
                  onChange={e => {
                    const v = e.target.value.replace(/\D/g, '');
                    const n = parseInt(v);
                    if (v === '' || (n >= 1 && n <= 31)) {
                      setDia(v);
                      if (v.length === 2) setTimeout(() => fechasMesRef.current?.focus(), 10);
                    }
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      fechasMesRef.current?.focus();
                    }
                  }}
                  placeholder="1"
                  className={inputCls}
                  autoComplete="off"
                />
              </div>
              <div>
                <label className={labelCls}>Mes (1-12)</label>
                <input
                  ref={fechasMesRef}
                  type="text"
                  inputMode="numeric"
                  maxLength={2}
                  value={mesNum}
                  onChange={e => {
                    const v = e.target.value.replace(/\D/g, '').slice(0, 2);
                    if (v === '') {
                      setMesNum('');
                      setMes('');
                      return;
                    }
                    const n = parseInt(v, 10);
                    if (isNaN(n) || n < 1 || n > 12) return;
                    setMesNum(v);
                    setMes(MESES[n - 1]);
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      montoRef.current?.focus();
                      setTimeout(() => {
                        try { montoRef.current?.select(); } catch {}
                      }, 0);
                    }
                  }}
                  placeholder="2"
                  className={inputCls}
                  autoComplete="off"
                />
                <p className="text-[10px] text-gray-500 mt-1 text-center">
                  {(() => {
                    const n = parseInt(mesNum || '', 10);
                    if (!isNaN(n) && n >= 1 && n <= 12) return MESES[n - 1];
                    return mes || '\u00A0';
                  })()}
                </p>
              </div>
            </div>
            <div>
              <label className={labelCls}>Monto ₡</label>
              <input
                ref={montoRef}
                type="text"
                inputMode="numeric"
                value={monto}
                onChange={e => setMonto(e.target.value.replace(/\D/g, ''))}
                onFocus={e => e.target.select()}
                placeholder="100"
                className={inputCls}
                autoComplete="off"
              />
            </div>
          </>
        );

      case 'PARLEY':
        return (
          <>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={labelCls}>Núm 1</label>
                <input
                  ref={firstRef}
                  type="text"
                  inputMode="numeric"
                  value={num1}
                  onChange={e => {
                    const clean = handleNumeroChange(e.target.value, 2, parleyNum2Ref);
                    setNum1(clean);
                    if (clean.length === 2) {
                      setTimeout(() => {
                        try { parleyNum2Ref.current?.select(); } catch {}
                      }, 20);
                    }
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      parleyNum2Ref.current?.focus();
                      setTimeout(() => {
                        try { parleyNum2Ref.current?.select(); } catch {}
                      }, 0);
                    }
                  }}
                  onFocus={e => e.target.select()}
                  placeholder="00"
                  className={inputCls}
                  autoComplete="off"
                />
              </div>
              <div>
                <label className={labelCls}>Núm 2</label>
                <input
                  ref={parleyNum2Ref}
                  type="text"
                  inputMode="numeric"
                  value={numero}
                  onChange={e => {
                    const clean = handleNumeroChange(e.target.value, 2, montoRef);
                    setNumero(clean);
                    if (clean.length === 2) {
                      setTimeout(() => {
                        try { montoRef.current?.select(); } catch {}
                      }, 20);
                    }
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      montoRef.current?.focus();
                      setTimeout(() => {
                        try { montoRef.current?.select(); } catch {}
                      }, 0);
                    }
                  }}
                  onFocus={e => e.target.select()}
                  placeholder="00"
                  className={inputCls}
                  autoComplete="off"
                />
              </div>
            </div>
            <div>
              <label className={labelCls}>Monto ₡</label>
              <input
                ref={montoRef}
                type="text"
                inputMode="numeric"
                value={monto}
                onChange={e => setMonto(e.target.value.replace(/\D/g, ''))}
                onFocus={e => e.target.select()}
                placeholder="100"
                className={inputCls}
                autoComplete="off"
              />
            </div>
          </>
        );
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      {renderForm()}
      <div className="flex gap-2 pt-2">
        {editingItem ? (
          <>
            <button
              type="button"
              onClick={onCancelEdit}
              className="flex-1 py-3 bg-gray-700 text-white font-bold rounded hover:bg-gray-600"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="flex-1 py-3 bg-yellow-500 text-black font-bold rounded hover:bg-yellow-400"
            >
              Guardar
            </button>
          </>
        ) : (
          <button
            type="submit"
            disabled={disabled}
            className={`w-full py-3 font-bold rounded text-lg ${
              disabled
                ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                : 'bg-green-600 text-white hover:bg-green-500'
            }`}
          >
            + Agregar
          </button>
        )}
      </div>
    </form>
  );
};

// ============================================
// TICKET PANEL COMPONENT
// ============================================
interface TicketPanelProps {
  sorteo: SorteoType;
  items: TicketItem[];
  onEdit: (item: TicketItem) => void;
  onRemove: (id: string) => void;
  onClear: () => void;
  onViewList?: () => void;
  showViewListButton?: boolean;
}

const TicketPanel: React.FC<TicketPanelProps> = ({
  sorteo,
  items,
  onEdit,
  onRemove,
  onClear,
  onViewList,
  showViewListButton = true,
}) => {
  const listRef = useRef<HTMLDivElement>(null);
  const total = items.reduce((sum, item) => sum + calcularTotalItem(item.data), 0);
  const sorteoConfig = SORTEO_CONFIG.find(s => s.id === sorteo)!;
  const modalidad = getSorteoModalidad(sorteo);

  const displayItems = (() => {
    // Orden visual por modalidad (sin cambiar el orden real de anotación)
    // REVENTADO: primero Rev1 solo (Rev2=0), luego los que llevan Rev2.
    if (modalidad === 'REVENTADO') {
      return items
        .map((it, idx) => ({ it, idx }))
        .sort((a, b) => {
          const aHasRev2 = a.it.data.modalidad === 'REVENTADO' && (a.it.data.montoRev2 ?? 0) > 0;
          const bHasRev2 = b.it.data.modalidad === 'REVENTADO' && (b.it.data.montoRev2 ?? 0) > 0;
          const aGroup = aHasRev2 ? 1 : 0;
          const bGroup = bHasRev2 ? 1 : 0;
          if (aGroup !== bGroup) return aGroup - bGroup;
          return a.idx - b.idx;
        })
        .map(x => x.it);
    }

    // MEGA: primero solo Rev1 (Rev2=0), luego Rev1+Rev2 (sin Num2), y de último los que llevan todo.
    if (modalidad === 'MEGA_REVENTADO') {
      return items
        .map((it, idx) => ({ it, idx }))
        .sort((a, b) => {
          const aData = a.it.data;
          const bData = b.it.data;

          const aRev2 = aData.modalidad === 'MEGA_REVENTADO' ? (aData.rev2 ?? 0) : 0;
          const bRev2 = bData.modalidad === 'MEGA_REVENTADO' ? (bData.rev2 ?? 0) : 0;
          const aHasNum2 = false;
          const bHasNum2 = false;

          const aGroup = aRev2 <= 0 ? 0 : (aHasNum2 ? 2 : 1);
          const bGroup = bRev2 <= 0 ? 0 : (bHasNum2 ? 2 : 1);
          if (aGroup !== bGroup) return aGroup - bGroup;
          return a.idx - b.idx;
        })
        .map(x => x.it);
    }

    return items;
  })();

  // Auto scroll to bottom
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [items.length]);

  type TicketColumn = { key: string; label: string; value: string; align?: 'left' | 'right' | 'center' };

  const getColumns = (data: TicketItemData): TicketColumn[] => {
    switch (data.modalidad) {
      case 'NORMAL':
        return [
          { key: 'numero', label: 'Número', value: `#${data.numero}`, align: 'center' },
          { key: 'monto', label: 'Monto', value: `₡${data.monto}`, align: 'right' },
        ];
      case 'REVENTADO':
        return [
          { key: 'numero', label: 'Número', value: `#${data.numero}`, align: 'center' },
          { key: 'rev1', label: 'Rev1', value: `₡${data.montoRev1}`, align: 'right' },
          { key: 'rev2', label: 'Rev2', value: data.montoRev2 > 0 ? `₡${data.montoRev2}` : '\u00A0', align: 'right' },
        ];
      case 'MEGA_REVENTADO':
        return [
          { key: 'n1', label: 'Número', value: `#${data.numero}`, align: 'center' },
          { key: 'rev1', label: 'Rev1', value: `₡${data.rev1}`, align: 'right' },
          { key: 'rev2', label: 'Rev2', value: data.rev2 > 0 ? `₡${data.rev2}` : '\u00A0', align: 'right' },
        ];
      case 'MONAZOS':
        return [
          { key: 'nums', label: 'Números', value: `${data.d1}${data.d2}${data.d3}`, align: 'center' },
          { key: 'tipo', label: 'Tipo', value: data.tipo, align: 'center' },
          { key: 'monto', label: 'Monto', value: `₡${data.monto}`, align: 'right' },
        ];
      case 'FECHAS':
        return [
          { key: 'fecha', label: 'Fecha', value: `${data.dia}-${data.mes.slice(0, 3)}`, align: 'center' },
          { key: 'monto', label: 'Monto', value: `₡${data.monto}`, align: 'right' },
        ];
      case 'PARLEY':
        return [
          { key: 'n1', label: 'Num1', value: `#${data.num1}`, align: 'center' },
          { key: 'n2', label: 'Num2', value: `#${data.num2}`, align: 'center' },
          { key: 'monto', label: 'Monto', value: `₡${data.monto}`, align: 'right' },
        ];
    }
  };

  const headerCols = (() => {
    switch (modalidad) {
      case 'NORMAL':
        return ['Número', 'Monto'];
      case 'REVENTADO':
        return ['Número', 'Rev1', 'Rev2'];
      case 'MEGA_REVENTADO':
        return ['Número', 'Rev1', 'Rev2'];
      case 'MONAZOS':
        return ['Números', 'Tipo', 'Monto'];
      case 'FECHAS':
        return ['Fecha', 'Monto'];
      case 'PARLEY':
        return ['Num1', 'Num2', 'Monto'];
      default:
        return ['Dato', 'Monto'];
    }
  })();

  const gridTemplate = (() => {
    switch (modalidad) {
      case 'NORMAL':
        return 'minmax(0, 1fr) minmax(0, 1fr)';
      case 'REVENTADO':
        return 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)';
      case 'MEGA_REVENTADO':
        return 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)';
      case 'MONAZOS':
        return 'minmax(0, 1fr) minmax(0, 0.8fr) minmax(0, 0.9fr)';
      case 'PARLEY':
        return 'minmax(0, 0.9fr) minmax(0, 0.9fr) minmax(0, 1fr)';
      case 'FECHAS':
        return 'minmax(0, 1fr) minmax(0, 1fr)';
      default:
        return `repeat(${headerCols.length}, minmax(0, 1fr))`;
    }
  })();

  return (
    <div className="flex flex-col h-full bg-gray-900 rounded-lg border border-gray-700">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-700">
        <div className="flex items-center gap-2">
          <span className={`${sorteoConfig.color} text-white text-xs font-bold px-2 py-0.5 rounded`}>
            {sorteoConfig.label}
          </span>
          <span className="text-gray-400 text-sm">
            {items.length} jugada{items.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {showViewListButton && (
            <button
              type="button"
              onClick={onViewList}
              className="text-xs font-bold text-gray-100 hover:text-white px-2.5 py-1.5 rounded border border-gray-700 bg-gray-800/60 hover:bg-gray-800/90"
            >
              Lista previa
            </button>
          )}
          {items.length > 0 && (
            <button
              onClick={onClear}
              className="text-xs text-red-400 hover:text-red-300"
            >
              Limpiar
            </button>
          )}
        </div>
      </div>

      {/* Items */}
      <div ref={listRef} className="flex-1 overflow-y-auto min-h-0">
        {items.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-600 text-sm">
            Sin jugadas
          </div>
        ) : (
          <div className="divide-y divide-gray-800">
            {/* Column header */}
            <div className="px-3 py-2 bg-gray-800/40 text-[10px] text-gray-400 uppercase tracking-wide">
              <div className="flex items-center gap-2">
                <div className="grid flex-1 gap-2" style={{ gridTemplateColumns: gridTemplate }}>
                  {headerCols.map(h => {
                    const isMoneyCol = h === 'Monto' || h === 'Rev1' || h === 'Rev2' || h === 'M';
                    return (
                      <span key={h} className={isMoneyCol ? 'text-right' : 'text-center'}>
                        {h}
                      </span>
                    );
                  })}
                </div>
                <div className="w-14 text-center">{modalidad === 'MONAZOS' ? 'X' : ''}</div>
              </div>
            </div>
            {displayItems.map(item => {
              const cols = getColumns(item.data);
              return (
                <div key={item.id} className="flex items-center px-3 py-2 hover:bg-gray-800/50 text-sm">
                  <div className="grid flex-1 gap-2" style={{ gridTemplateColumns: gridTemplate }}>
                    {cols.map(c => (
                      <span
                        key={c.key}
                        className={`font-mono ${c.key === 'monto' || c.key.startsWith('rev') ? 'text-green-400' : 'text-white'} ${
                          c.align === 'right' ? 'text-right' : c.align === 'center' ? 'text-center' : 'text-left'
                        }`}
                      >
                        {c.value}
                      </span>
                    ))}
                  </div>
                  <div className="w-14 flex items-center justify-end gap-1">
                    <button
                      onClick={() => onEdit(item)}
                      className="p-1 text-gray-500 hover:text-yellow-400"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      onClick={() => onRemove(item.id)}
                      className="p-1 text-gray-500 hover:text-red-400"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Total */}
      <div className="border-t border-gray-700 px-3 py-3 bg-gray-800">
        <div className="flex items-center justify-between">
          <span className="text-gray-400 text-sm">TOTAL</span>
          <span className="text-2xl font-bold text-green-400 font-mono">
            ₡{total.toLocaleString()}
          </span>
        </div>
      </div>
    </div>
  );
};

// ============================================
// PAYMENT SECTION
// ============================================
interface PaymentSectionProps {
  method: PaymentMethod;
  onMethodChange: (m: PaymentMethod) => void;
  total: number;
  onConfirm: () => void;
  disabled: boolean;
  showCredit: boolean;
  cliente: Cliente | null;
  onClienteChange: (c: Cliente | null) => void;
  referencia: string;
  onReferenciaChange: (r: string) => void;
}

const PaymentSection: React.FC<PaymentSectionProps> = ({
  method,
  onMethodChange,
  total,
  onConfirm,
  disabled,
  showCredit,
  cliente,
  onClienteChange,
  referencia,
  onReferenciaChange,
}) => {
  const [showClienteModal, setShowClienteModal] = useState(false);
  const [clientesList, setClientesList] = useState<Cliente[]>([]);

  useEffect(() => {
    creditoService.listarClientes().then(data => {
      setClientesList(data.filter(c => c.activo !== false && !c.bloqueado_credito));
    }).catch(() => setClientesList([]));
  }, []);

  const methods: { id: PaymentMethod; label: string }[] = [
    { id: 'EFECTIVO', label: 'Efectivo' },
    { id: 'SINPE', label: 'SINPE' },
    { id: 'TRANSFERENCIA', label: 'Transfer' },
    ...(showCredit ? [{ id: 'CREDITO' as PaymentMethod, label: 'Crédito' }] : []),
  ];

  const isCredito = method === 'CREDITO';
  const creditoValid = !isCredito || (cliente && referencia.trim());

  return (
    <div className="bg-gray-800 p-3 rounded-lg space-y-3">
      {/* Payment methods */}
      <div className="flex gap-1">
        {methods.map(m => (
          <button
            key={m.id}
            onClick={() => onMethodChange(m.id)}
            className={`
              flex-1 py-2 text-xs font-bold rounded transition-all
              ${method === m.id
                ? 'bg-yellow-500 text-black'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }
            `}
          >
            {m.label}
          </button>
        ))}
      </div>

      {/* Credit options */}
      {isCredito && (
        <div className="space-y-2 p-2 bg-purple-900/30 rounded border border-purple-700">
          <button
            type="button"
            onClick={() => setShowClienteModal(true)}
            className="w-full py-2 text-sm bg-gray-700 text-white rounded hover:bg-gray-600 flex items-center justify-center gap-2"
          >
            <User size={16} />
            {cliente ? cliente.nombre : 'Seleccionar cliente'}
          </button>
          <input
            type="text"
            value={referencia}
            onChange={e => onReferenciaChange(e.target.value)}
            placeholder="Referencia (obligatorio)"
            className="w-full px-3 py-2 text-sm bg-gray-900 border border-gray-600 rounded text-white"
          />
          {(!cliente || !referencia.trim()) && (
            <p className="text-xs text-amber-400">⚠️ Cliente y referencia requeridos</p>
          )}
        </div>
      )}

      {/* Confirm button */}
      <button
        onClick={onConfirm}
        disabled={disabled || !creditoValid}
        className={`
          w-full py-4 text-lg font-bold rounded transition-all
          ${disabled || !creditoValid
            ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
            : 'bg-green-600 text-white hover:bg-green-500'
          }
        `}
      >
        Confirmar ₡{total.toLocaleString()}
      </button>

      {/* Cliente Modal */}
      <Modal
        isOpen={showClienteModal}
        onClose={() => setShowClienteModal(false)}
        title="Seleccionar Cliente"
      >
        <div className="space-y-2 max-h-60 overflow-y-auto">
          {clientesList.length === 0 ? (
            <p className="text-center text-gray-500 py-4 text-sm">No hay clientes disponibles</p>
          ) : clientesList.map(c => (
            <button
              key={c.id}
              onClick={() => {
                onClienteChange(c);
                setShowClienteModal(false);
              }}
              className={`
                w-full p-3 text-left rounded border transition-all
                ${cliente?.id === c.id
                  ? 'bg-purple-100 border-purple-500'
                  : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                }
              `}
            >
              <p className="font-medium">{c.nombre}</p>
              <p className="text-xs text-gray-500">{c.telefono}</p>
            </button>
          ))}
        </div>
      </Modal>
    </div>
  );
};

// ============================================
// MAIN POS TERMINAL PAGE
// ============================================
export const POSTerminal: React.FC = () => {
  const { user } = useAuth();
  const [limiteApuestaMaxima, setLimiteApuestaMaxima] = useState<number | null>(
    user?.limiteApuestaMaxima ?? null
  );
  const limiteApuestaLabel = limiteApuestaMaxima == null
    ? 'Sin límite'
    : `₡${limiteApuestaMaxima.toLocaleString()}`;

  // State
  const [activeSorteo, setActiveSorteo] = useState<SorteoType>('TICA');
  const [ticketsBySorteo, setTicketsBySorteo] = useState<TicketsBySorteo>(createEmptyTicketsBySorteo);
  const [clienteNombreModal, setClienteNombreModal] = useState('');
  const [editingItem, setEditingItem] = useState<TicketItem | null>(null);
  const [successModal, setSuccessModal] = useState(false);
  const [lastTicketCode, setLastTicketCode] = useState('');
  const [amountResetKey, setAmountResetKey] = useState(0);
  const [paymentModal, setPaymentModal] = useState(false);
  const modalClienteNombreInputRef = useRef<HTMLInputElement>(null);
  const pendingClienteNombreRef = useRef<string>('');
  const [paymentError, setPaymentError] = useState<string | null>(null);
  // Toast ligero (no bloqueante) para avisos de validación de monto.
  const [montoToast, setMontoToast] = useState<string | null>(null);
  const [montoToastVisible, setMontoToastVisible] = useState(false);
  const montoToastTimers = useRef<{ fade?: ReturnType<typeof setTimeout>; clear?: ReturnType<typeof setTimeout> }>({});
  const mostrarMontoToast = useCallback((mensaje: string) => {
    if (montoToastTimers.current.fade) clearTimeout(montoToastTimers.current.fade);
    if (montoToastTimers.current.clear) clearTimeout(montoToastTimers.current.clear);
    setMontoToast(mensaje);
    setMontoToastVisible(true);
    // Inicia el desvanecido a los 1.2s y elimina el nodo a los 1.5s.
    montoToastTimers.current.fade = setTimeout(() => setMontoToastVisible(false), 1200);
    montoToastTimers.current.clear = setTimeout(() => setMontoToast(null), 1500);
  }, []);
  useEffect(() => () => {
    if (montoToastTimers.current.fade) clearTimeout(montoToastTimers.current.fade);
    if (montoToastTimers.current.clear) clearTimeout(montoToastTimers.current.clear);
  }, []);
  const [paymentNotice, setPaymentNotice] = useState<string | null>(null);
  const [riesgoAdvertencia, setRiesgoAdvertencia] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const ventaEnProcesoRef = useRef(false);
  const [sharingWhatsApp, setSharingWhatsApp] = useState(false);
  const pendingWhatsAppRef = useRef(false);

  // Backend sorteos state
  const [backendSorteos, setBackendSorteos] = useState<BackendSorteo[]>([]);
  const [selectedSorteoId, setSelectedSorteoId] = useState<Record<SorteoType, string>>({
    TICA: '', REVENTADO: '', MEGA: '', MONAZOS: '', FECHAS: '', PARLEY: '',
  });
  const seleccionarMasProximoPendienteRef = useRef(true);

  // ── Venta futura ──
  const [ventaFutura, setVentaFutura] = useState(false);
  const [fechaFutura, setFechaFutura] = useState('');
  const ventaFuturaRef = useRef(false);
  ventaFuturaRef.current = ventaFutura;
  const fechaFuturaRef = useRef('');
  fechaFuturaRef.current = fechaFutura;

  const [ticketListModal, setTicketListModal] = useState(false);

  // Sidebar live data
  const [sidebarTop, setSidebarTop] = useState<Array<{ numero: string; total: number }>>([]);
  const [sidebarRestringidos, setSidebarRestringidos] = useState<string[]>([]);
  const [sidebarLimites, setSidebarLimites] = useState<{
    monto_minimo: number | null;
    monto_maximo: number | null;
    limite_por_numero: number | null;
    limite_total: number | null;
  } | null>(null);

  // Recent tickets for sidebar (últimos 10 del local)
  const [recentTickets, setRecentTickets] = useState<Array<{
    id: string; ticket_number: string | null; cliente_nombre: string | null;
    created_at: string; total: number; estado: string;
  }>>([]);
  const [loadingRecentTickets, setLoadingRecentTickets] = useState(false);

  const [anularTicketModal, setAnularTicketModal] = useState(false);
  const [reimpresionModal, setReimpresionModal] = useState(false);
  const [sidebarPreviewPayload, setSidebarPreviewPayload] = useState<TicketPayload | null>(null);
  const [localEsPropio, setLocalEsPropio] = useState<boolean | null>(null);

  // Estado para imagen de ticket generada (No Propios)
  const [ticketImageUrl, setTicketImageUrl] = useState<string | null>(null);
  const [ticketImageBlob, setTicketImageBlob] = useState<Blob | null>(null);
  const [ticketImageCode, setTicketImageCode] = useState<string>('');
  const [ticketImageTotal, setTicketImageTotal] = useState<number>(0);
  const [ticketImagePayload, setTicketImagePayload] = useState<TicketPayload | null>(null);

  // Estado de crédito para bloqueo por saldo (solo NO PROPIOS)
  const [estadoCredito, setEstadoCredito] = useState<{
    aplica_limite_saldo: boolean;
    bloqueado_por_saldo: boolean;
    mensaje_bloqueo: string | null;
    nivel_alerta: string | null;
    disponible: string | null;
    limite_saldo: string | null;
    porcentaje_restante: number | null;
    saldo_real: string | null;
    saldo_post_sorteo: string | null;
  } | null>(null);

  const cargarEstadoCredito = () => {
    fetchAPI<any>('/saldos/estado-credito')
      .then((data) => setEstadoCredito(data))
      .catch(() => {});
  };

  const esMovil = useIsMobile();

  useEffect(() => {
    fetchAPI<{ es_propio?: boolean }>('/operaciones/local/ticket-config')
      .then((data) => setLocalEsPropio(Boolean(data.es_propio)))
      .catch(() => {});
    cargarEstadoCredito();
  }, []);

  useEffect(() => {
    fetchAPI<{ monto: number | null }>('/configuracion/maximo-apuesta')
      .then((data) => setLimiteApuestaMaxima(data.monto ?? null))
      .catch(() => {
        setLimiteApuestaMaxima(user?.limiteApuestaMaxima ?? null);
      });
  }, [user?.limiteApuestaMaxima]);

  // Ticket preview/print state
  const [ticketPreviewPayload, setTicketPreviewPayload] = useState<TicketPayload | null>(null);

  const [pullTicketModal, setPullTicketModal] = useState(false);
  const [pullTicketCode, setPullTicketCode] = useState('');
  const [pullTicketError, setPullTicketError] = useState<string | null>(null);
  const pullTicketInputRef = useRef<HTMLInputElement>(null);

  const ticketHistoryRef = useRef<
    Map<string, { sorteo: SorteoType; createdAt: number; items: TicketItemData[] }>
  >(new Map());

  // Ref para handleConfirm (para usar en keyboard shortcuts)
  const handleConfirmRef = useRef<() => Promise<string | null>>(null);

  // Auto-focus al input de cliente opcional cuando se abre el modal de venta
  // Solo en web — en APK nativo el teclado tapa el modal
  useEffect(() => {
    if (!paymentModal || isNativePlatform()) return;
    const timer = setTimeout(() => {
      modalClienteNombreInputRef.current?.focus();
    }, 80);

    return () => clearTimeout(timer);
  }, [paymentModal]);

  const currentItems = ticketsBySorteo[activeSorteo];
  const total = currentItems.reduce((sum, item) => sum + calcularTotalItem(item.data), 0);

  // Sorteos agrupados por tipo para el tab activo
  const sorteosByTipo = useCallback((tipo: SorteoType): BackendSorteo[] => {
    const tipoId = tipoIdFromSorteoType(tipo);
    if (ventaFutura && fechaFutura) {
      // Venta futura: mostrar todos los sorteos que aplican para esa fecha
      // (el backend ya filtró por _fecha_aplica al pasar ?fecha=)
      return backendSorteos.filter(s => s.tipo_id === tipoId);
    }
    // Modo normal: solo sorteos ABIERTOS
    return backendSorteos.filter(s => s.tipo_id === tipoId && s.estado === 'ABIERTO');
  }, [backendSorteos, ventaFutura, fechaFutura]);

  const currentSorteoId = selectedSorteoId[activeSorteo];
  const currentSorteoSeleccionado = backendSorteos.find(s => s.id === currentSorteoId) || null;
  const sorteoVentaNoDisponible = !currentSorteoId || !currentSorteoSeleccionado;

  // ── Sorteo cerrado (local countdown expiró) ──
  const [sorteoCerradoLocal, setSorteoCerradoLocal] = useState(false);

  useEffect(() => {
    setSorteoCerradoLocal(false);
  }, [activeSorteo, currentSorteoId]);

  const isSorteoClosed = (() => {
    // En modo venta futura, el sorteo no está cerrado (se venderá en otra fecha)
    if (ventaFutura && fechaFutura) return false;
    const currentBackend = backendSorteos.find(s => s.id === currentSorteoId);
    return currentBackend?.estado !== 'ABIERTO' || sorteoCerradoLocal;
  })();

  const isSorteoClosedRef = useRef(false);
  isSorteoClosedRef.current = isSorteoClosed;

  const paymentModalRef = useRef(paymentModal);
  paymentModalRef.current = paymentModal;

  const currentItemsRef = useRef(currentItems);
  currentItemsRef.current = currentItems;

  const cargarSorteosBackend = useCallback(() => {
    const url = ventaFuturaRef.current && fechaFuturaRef.current
      ? `/sorteos?fecha=${fechaFuturaRef.current}`
      : '/sorteos';
    const esModofuturo = ventaFuturaRef.current && fechaFuturaRef.current;
    fetchAPI<BackendSorteo[]>(url, { timeoutMs: 20000 })
      .then((sorteos) => {
        const debeSeleccionarMasProximo = !esModofuturo && seleccionarMasProximoPendienteRef.current;
        const sorteoMasProximo = debeSeleccionarMasProximo ? obtenerSorteoMasProximoACerrar(sorteos) : null;
        const tipoMasProximo = sorteoMasProximo ? tipoPosDesdeTipoId(sorteoMasProximo.tipo_id) : null;

        setBackendSorteos(sorteos);
        if (tipoMasProximo) {
          setActiveSorteo(tipoMasProximo);
        }
        setSelectedSorteoId((prev) => {
          const next = { ...prev };
          if (debeSeleccionarMasProximo) {
            for (const tipo of SORTEO_CONFIG) {
              const tipoId = tipoIdFromSorteoType(tipo.id);
              next[tipo.id] = obtenerSorteoMasProximoPorTipo(sorteos, tipoId)?.id || '';
            }

            seleccionarMasProximoPendienteRef.current = false;
            return next;
          }

          for (const tipo of SORTEO_CONFIG) {
            const tipoId = tipoIdFromSorteoType(tipo.id);
            const matching = esModofuturo
              ? sorteos.filter(s => s.tipo_id === tipoId)
              : obtenerSorteosAbiertosDisponibles(sorteos).filter(s => s.tipo_id === tipoId);
            const current = next[tipo.id];
            const sigueVigente = current && matching.some(s => s.id === current);
            if (!sigueVigente) {
              next[tipo.id] = esModofuturo
                ? (matching.length > 0 ? matching[0].id : '')
                : (obtenerSorteoMasProximoPorTipo(sorteos, tipoId)?.id || '');
            }
          }
          return next;
        });
      })
      .catch((err) => {
        if (err?.message !== 'Conexion lenta. Intente nuevamente.') {
          console.error('Error cargando sorteos:', err);
        }
      });
  }, []);

  // Fetch sorteos from backend on mount
  useEffect(() => {
    cargarSorteosBackend();
    const interval = window.setInterval(cargarSorteosBackend, 15000);
    const onFocus = () => cargarSorteosBackend();
    window.addEventListener('focus', onFocus);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [cargarSorteosBackend]);

  // Sidebar: fetch top-vendidos + restringidos when sorteo changes
  useEffect(() => {
    if (!currentSorteoId) {
      setSidebarTop([]);
      setSidebarRestringidos([]);
      setSidebarLimites(null);
      return;
    }
    const load = () => {
      fetchAPI<{ top: Array<{ numero: string; total: number }>; restringidos: string[]; limites: { monto_minimo: number | null; monto_maximo: number | null; limite_por_numero: number | null; limite_total: number | null } | null }>(
        `/listas/top-vendidos/${currentSorteoId}`
      )
        .then((data) => {
          setSidebarTop(data.top || []);
          setSidebarRestringidos(data.restringidos || []);
          setSidebarLimites(data.limites || null);
        })
        .catch(() => {
          setSidebarTop([]);
          setSidebarRestringidos([]);
          setSidebarLimites(null);
        });
    };
    load();
    const iv = window.setInterval(load, 30000);
    return () => window.clearInterval(iv);
  }, [currentSorteoId]);

  // Sidebar: últimos 10 tickets del local (fetch on mount + after each sale)
  const fetchRecentTickets = useCallback(() => {
    setLoadingRecentTickets(true);
    fetchAPI<Array<{ id: string; ticket_number: string | null; cliente_nombre: string | null; created_at: string; total: number; estado: string }>>('/tickets/recientes-sidebar?limit=10')
      .then(data => setRecentTickets(data))
      .catch(() => setRecentTickets([]))
      .finally(() => setLoadingRecentTickets(false));
  }, []);

  useEffect(() => {
    fetchRecentTickets();
  }, [fetchRecentTickets]);

  // Refrescar "Últimos tickets" al confirmarse una venta (evento del bus interno).
  useEffect(() => {
    const unsubscribe = onVenta(() => {
      fetchRecentTickets();
    });
    return unsubscribe;
  }, [fetchRecentTickets]);

  // Handler: click on recent ticket row → open preview via detalle endpoint (read-only)
  const handlePreviewRecentTicket = useCallback(async (ticket: { id: string; ticket_number: string | null }) => {
    const code = ticket.ticket_number || ticket.id;
    try {
      const data = await fetchAPI<any>(`/tickets/${encodeURIComponent(code)}/detalle`);
      const jugadas: TicketJugada[] = (data.jugadas || []).map((j: any) => ({
        num1: j.num1,
        num2: j.num2 || undefined,
        monto: j.monto,
      }));
      const payload = await procesarReimpresion({
        ticketCode: formatTicketForPrint(data.ticket_number),
        ticketId: data.ticket_number,
        sorteoNombre: data.sorteo_nombre,
        sorteoCierre: data.sorteo_cierre,
        sorteoFecha: data.sorteo_fecha,
        metodoPago: data.metodo_pago,
        referencia: data.referencia,
        total: data.total,
        // Fecha/hora original de venta — la copia debe ser idéntica al original.
        fecha: data.fecha || null,
        jugadas,
        local: {
          nombre: data.local?.nombre || '',
          descripcion: data.local?.descripcion || null,
          telefono: data.local?.telefono || null,
          logo: data.local?.logo || null,
          esPropio: data.local?.es_propio === true,
          grupoId: data.local?.grupo_id || null,
        },
        esPropio: data.local?.es_propio === true,
        clienteNombre: data.cliente_nombre || 'Cliente',
        fechaOperativa: data.fecha_operativa || null,
        estado: data.estado || null,
        fechaAnulacion: data.fecha_anulacion || null,
      });
      setSidebarPreviewPayload(payload);
    } catch (e: any) {
      console.error('[POSTerminal] Error al abrir preview:', e);
      alert(e?.message || 'No se pudo abrir el detalle del ticket.');
    }
  }, []);

  // Cargar historial desde localStorage (si existe)
  useEffect(() => {
    try {
      const raw = localStorage.getItem('pos.ticketHistory.v1');
      if (!raw) return;
      const arr = JSON.parse(raw) as Array<{ code: string; sorteo: SorteoType; createdAt: number; items: TicketItemData[] }>;
      if (!Array.isArray(arr)) return;
      const map = new Map<string, { sorteo: SorteoType; createdAt: number; items: TicketItemData[] }>();
      for (const it of arr) {
        if (!it?.code || !it?.sorteo || !Array.isArray(it.items)) continue;
        map.set(String(it.code).toUpperCase(), { sorteo: it.sorteo, createdAt: it.createdAt || Date.now(), items: it.items });
      }
      ticketHistoryRef.current = map;
    } catch {
      // ignore
    }
  }, []);

  const persistTicketHistory = useCallback(() => {
    try {
      const entries: Array<{ code: string; sorteo: SorteoType; createdAt: number; items: TicketItemData[] }> = [];
      for (const [code, v] of ticketHistoryRef.current.entries()) {
        entries.push({ code, sorteo: v.sorteo, createdAt: v.createdAt, items: v.items });
      }
      entries.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
      localStorage.setItem('pos.ticketHistory.v1', JSON.stringify(entries.slice(0, 50)));
    } catch {
      // ignore
    }
  }, []);

  // Get ticket counts
  const ticketCounts = Object.fromEntries(
    SORTEO_CONFIG.map(s => [s.id, ticketsBySorteo[s.id].length])
  ) as Record<SorteoType, number>;

  // Handlers
  const handleAddItem = useCallback((data: TicketItemData) => {
    // Regla existente: monto mínimo ₡50 (se conserva intacta).
    if (tieneMontoMenorAlMinimo(data)) {
      setPaymentError(MENSAJE_MONTO_MINIMO_JUGADA);
      mostrarMontoToast(MENSAJE_MONTO_MINIMO_TOAST);
      return;
    }
    // Regla nueva: el monto solo puede ser múltiplo de 5.
    if (tieneMontoNoMultiploDe5(data)) {
      mostrarMontoToast(MENSAJE_MONTO_MULTIPLO_TOAST);
      return;
    }
    setPaymentError(null);
    const newItem: TicketItem = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      data,
      timestamp: Date.now(),
    };
    setTicketsBySorteo(prev => ({
      ...prev,
      [activeSorteo]: [...prev[activeSorteo], newItem],
    }));
  }, [activeSorteo, mostrarMontoToast]);

  const handleEditItem = useCallback((item: TicketItem) => {
    setEditingItem(item);
  }, []);

  const handleSaveEdit = useCallback((data: TicketItemData) => {
    if (!editingItem) return;
    if (tieneMontoMenorAlMinimo(data)) {
      setPaymentError(MENSAJE_MONTO_MINIMO_JUGADA);
      mostrarMontoToast(MENSAJE_MONTO_MINIMO_TOAST);
      return;
    }
    if (tieneMontoNoMultiploDe5(data)) {
      mostrarMontoToast(MENSAJE_MONTO_MULTIPLO_TOAST);
      return;
    }
    setPaymentError(null);
    setTicketsBySorteo(prev => ({
      ...prev,
      [activeSorteo]: prev[activeSorteo].map(item =>
        item.id === editingItem.id ? { ...item, data } : item
      ),
    }));
    setEditingItem(null);
  }, [activeSorteo, editingItem, mostrarMontoToast]);

  const handleRemoveItem = useCallback((id: string) => {
    setTicketsBySorteo(prev => {
      const updated = { ...prev };
      for (const key of Object.keys(updated) as SorteoType[]) {
        const before = updated[key].length;
        updated[key] = updated[key].filter(item => item.id !== id);
        if (updated[key].length < before) break; // found and removed
      }
      return updated;
    });
  }, []);

  const handleClearTicket = useCallback(() => {
    setTicketsBySorteo(prev => ({
      ...prev,
      [activeSorteo]: [],
    }));

    setEditingItem(null);
    setAmountResetKey(prev => prev + 1);
  }, [activeSorteo, setEditingItem, setAmountResetKey]);

  const pullTicketByCode = useCallback(async (rawCode: string) => {
    const code = rawCode.replace(/\D/g, '').trim();
    if (!code) {
      setPullTicketError('Ingresa el número de ticket');
      return;
    }

    let found: { sorteo: SorteoType; createdAt: number; items: TicketItemData[] } | undefined;

    // 1. Buscar en memoria (Map)
    found = ticketHistoryRef.current.get(code);

    // 2. Fallback a localStorage
    if (!found) {
      try {
        const raw = localStorage.getItem('pos.ticketHistory.v1');
        if (raw) {
          const arr = JSON.parse(raw) as Array<{ code: string; sorteo: SorteoType; createdAt: number; items: TicketItemData[] }>;
          const hit = Array.isArray(arr)
            ? arr.find(x => String(x.code || '').replace(/\D/g, '') === code)
            : undefined;
          if (hit && Array.isArray(hit.items)) {
            found = { sorteo: hit.sorteo, createdAt: hit.createdAt || Date.now(), items: hit.items };
            ticketHistoryRef.current.set(code, found);
          }
        }
      } catch {
        // ignore
      }
    }

    // 3. Fallback a backend — permite jalar tickets de sorteos cerrados/viejos/otro dispositivo
    if (!found) {
      try {
        setPullTicketError(null);
        const resp = await fetchAPI<{
          ticket_number: string;
          tipo_id: string;
          jugadas: Array<{ num1: string; num2: string | null; monto: number }>;
        }>(`/tickets/jalar/${encodeURIComponent(code)}`);

        if (resp && resp.jugadas && resp.jugadas.length > 0) {
          // Convertir tipo_id backend → SorteoType frontend
          const backendTipo = (resp.tipo_id || '').toUpperCase();
          const sorteoType: SorteoType = backendTipo === 'NORMAL' ? 'TICA' : backendTipo as SorteoType;

          // Convertir jugadas backend → TicketItemData frontend
          const items = jugadasToItems(resp.jugadas, sorteoType);
          if (items.length > 0) {
            found = { sorteo: sorteoType, createdAt: Date.now(), items };
            ticketHistoryRef.current.set(code, found);
          }
        }
      } catch (e: any) {
        // El backend explica POR QUÉ no se puede jalar (410 >30 días, 422 anulado,
        // 422 número corto ambiguo, 403 fuera de alcance). Antes se tragaba todo
        // y siempre se mostraba "Ticket no encontrado", que ocultaba la causa real.
        setPullTicketError(e?.message || 'Ticket no encontrado');
        return;
      }
    }

    if (!found) {
      setPullTicketError('Ticket no encontrado');
      return;
    }

    const sourceSorteo = found.sorteo;
    if (sourceSorteo !== activeSorteo) {
      setPullTicketError('Este ticket pertenece a otro tipo de sorteo y no puede ser cargado aquí.');
      return;
    }

    // TAREA 3 — Descartar jugadas MONAZOS con subtipos obsoletos (DESORDEN, P2, U2)
    let itemsToLoad = found.items;
    let omitidosObsoletos = false;
    if (sourceSorteo === 'MONAZOS') {
      const filtrados = itemsToLoad.filter(
        item => item.modalidad !== 'MONAZOS' || item.tipo === 'ORDEN'
      );
      if (filtrados.length < itemsToLoad.length) {
        omitidosObsoletos = true;
        itemsToLoad = filtrados;
      }
    }

    const rebuiltItems: TicketItem[] = itemsToLoad.map(data => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      data,
      timestamp: Date.now(),
    }));

    // TAREA 4 — Limpiar jugadas actuales antes de cargar las del ticket jalado
    handleClearTicket();
    setTicketsBySorteo(prev => ({
      ...prev,
      [activeSorteo]: rebuiltItems,
    }));
    setPullTicketModal(false);
    setPullTicketCode('');
    // TAREA 3 — Mostrar aviso si se omitieron subtipos obsoletos
    setPullTicketError(omitidosObsoletos ? 'El ticket contiene jugadas obsoletas (Desorden/P2/U2) que fueron omitidas.' : null);
    // Importante: solo cargamos los números para editar; NO confirmamos venta.
    setPaymentModal(false);
    setPaymentError(null);
    setPaymentNotice(null);
  }, [activeSorteo, handleClearTicket]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const isTypingInInput = 
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable ||
        false;

      const isPrintKey = e.key === '*' || e.code === 'NumpadMultiply';

      if (isTypingInInput && !isPrintKey && e.key !== 'Escape') {
        if (!(e.key === 'Enter' && paymentModalRef.current)) {
          return;
        }
      }

      if (e.key === 'Escape') {
        if (sidebarPreviewPayload) { setSidebarPreviewPayload(null); return; }
        if (ticketPreviewPayload) { setTicketPreviewPayload(null); return; }
        if (successModal) setSuccessModal(false);
        if (paymentModalRef.current) setPaymentModal(false);
        if (pullTicketModal) setPullTicketModal(false);
        if (anularTicketModal) setAnularTicketModal(false);
        if (reimpresionModal) setReimpresionModal(false);
        return;
      }

      if (sidebarPreviewPayload || ticketPreviewPayload || successModal || pullTicketModal || anularTicketModal || reimpresionModal) return;

      if ((isPrintKey || e.key === 'Enter') && paymentModalRef.current) {
        e.preventDefault();
        if (isSorteoClosedRef.current) return;

        if (currentItemsRef.current.length === 0) {
          setPaymentError('No hay jugadas. Usa "Jalar ticket" o captura una jugada.');
          return;
        }

        pendingClienteNombreRef.current = clienteNombreModal.trim();
        setPaymentError(null);
        setPaymentNotice(null);
        setRiesgoAdvertencia(null);
        setPaymentModal(false);
        handleConfirmRef.current?.();
        return;
      }

      if (isPrintKey && !paymentModalRef.current) {
        e.preventDefault();
        if (isSorteoClosedRef.current) return;
        setClienteNombreModal('');
        setPaymentError(null);
        setPaymentNotice(null);
        setRiesgoAdvertencia(null);
        setPaymentModal(true);
        return;
      }

      if (e.key === '-' && !paymentModalRef.current) {
        e.preventDefault();
        handleClearTicket();
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [clienteNombreModal]);

  const handleConfirm = useCallback(async (): Promise<string | null> => {
    // Un ref bloquea reentradas inmediatamente, antes del siguiente render.
    if (!intentarIniciarVenta(ventaEnProcesoRef)) return null;

    try {
    if (currentItems.length === 0) return null;

    const sorteoId = selectedSorteoId[activeSorteo];
    if (!sorteoId) {
      setPaymentError('No hay sorteo seleccionado para este tipo');
      setPaymentModal(true);
      return null;
    }
    if (!ventaFutura && !backendSorteos.some(s => s.id === sorteoId)) {
      setPaymentError('No hay sorteo seleccionado para este tipo');
      setPaymentModal(true);
      return null;
    }

    const jugadas = itemsToJugadas(currentItems.map(i => i.data));
    if (jugadas.length === 0) return null;

    const jugadaMenorAlMinimo = jugadas.find(jugada => Number(jugada.monto) < MONTO_MINIMO_JUGADA);
    if (jugadaMenorAlMinimo) {
      setPaymentError(MENSAJE_MONTO_MINIMO_JUGADA);
      setPaymentModal(true);
      return null;
    }

    if (limiteApuestaMaxima != null) {
      const jugadaExcedida = jugadas.find((jugada) => Number(jugada.monto) > limiteApuestaMaxima);
      if (jugadaExcedida) {
        const mensaje = `Excede el máximo permitido. Máximo por apuesta: ₡${limiteApuestaMaxima.toLocaleString()}`;
        setPaymentError(mensaje);
        setPaymentModal(true);
        return null;
      }
    }

    try {
      const montoPorNumero = new Map<string, number>();
      for (const jugada of jugadas) {
        montoPorNumero.set(
          jugada.num1,
          (montoPorNumero.get(jugada.num1) || 0) + Number(jugada.monto || 0),
        );
      }

      const revisiones = await Promise.all(
        Array.from(montoPorNumero.entries()).map(([numero, monto]) =>
          fetchRiesgoNumero(numero, monto, sorteoId)
            .then(resultado => ({ numero, resultado }))
            .catch(() => null)
        )
      );

      const evaluaciones = revisiones.filter(
        (item): item is { numero: string; resultado: Awaited<ReturnType<typeof fetchRiesgoNumero>> } => item !== null
      );

      const riesgoAlto = evaluaciones.find(item => item.resultado.nivel === 'ALTO');
      const riesgoMedio = evaluaciones.find(item => item.resultado.nivel === 'MEDIO');

      if (riesgoAlto) {
        setRiesgoAdvertencia(
          riesgoAlto.resultado.mensaje ||
          `Advertencia: el número ${riesgoAlto.numero} queda con riesgo ALTO.`
        );
      } else if (riesgoMedio) {
        setRiesgoAdvertencia(
          riesgoMedio.resultado.mensaje ||
          `Advertencia: el número ${riesgoMedio.numero} queda con riesgo MEDIO.`
        );
      } else {
        setRiesgoAdvertencia(null);
      }
    } catch {
      setRiesgoAdvertencia(null);
    }

    setSubmitting(true);
    try {
      const clienteNombreCapturado = pendingClienteNombreRef.current || clienteNombreModal.trim();
      const totalVenta = currentItems.reduce((sum, item) => sum + calcularTotalItem(item.data), 0);

      // Misma llave en todos los reintentos del MISMO carrito: si la petición
      // anterior llegó al backend pero la respuesta se perdió (timeout, red
      // caída), el reintento devuelve ESE ticket en vez de crear un segundo.
      // Si el vendedor corrigió las jugadas, la huella cambia y se genera una
      // llave nueva.
      const idempotencyKey = obtenerIdempotencyKeyDeVenta(
        huellaDeVenta({
          sorteoId,
          jugadas,
          clienteNombre: clienteNombreCapturado || null,
          fechaFutura: ventaFutura && fechaFutura ? fechaFutura : null,
        }),
      );

      const ticket = await fetchAPI<any>('/tickets', {
        method: 'POST',
        // La venta es la petición que NO se puede reintentar a ciegas. Se le da
        // margen amplio para que un 3G malo termine de responder en vez de que
        // el cliente aborte y el vendedor reintente sobre un ticket ya creado.
        timeoutMs: 45_000,
        headers: {
          'X-POS-Client': 'tiempos-pos-v2',
          'Idempotency-Key': idempotencyKey,
        },
        body: JSON.stringify({
          sorteo_id: sorteoId,
          limiteApuestaMaxima,
          local_id: user?.localId || 'LOCAL-1',
          metodo_pago: 'EFECTIVO',
          cliente_id: null,
          cliente_nombre: clienteNombreCapturado || null,
          referencia: null,
          jugadas,
          ...(ventaFutura && fechaFutura ? { fecha_operativa: fechaFutura } : {}),
        }),
      });

      const code = ticket.ticket_number || ticket.id;

      // Venta confirmada por el backend: recién ahora se cierra el intento y se
      // descarta la llave. Si esto falla antes, la llave sobrevive a propósito.
      limpiarIdempotencyKeyDeVenta();

      // Notificar a listas para refresh optimista inmediato (sin esperar polling)
      emitirVenta({
        sorteo_id: sorteoId,
        tipo_id: activeSorteo,
        jugadas: jugadas.map((j: any) => ({ num1: j.num1, num2: j.num2 ?? null, monto: j.monto })),
      });

      // Guardar historial local para poder "jalar" el ticket luego
      ticketHistoryRef.current.set(code, {
        sorteo: activeSorteo,
        createdAt: Date.now(),
        items: currentItems.map(i => i.data),
      });
      persistTicketHistory();

      setLastTicketCode(code);
      // Refrescar estado de crédito tras cada venta
      cargarEstadoCredito();
      const sorteoInfo = backendSorteos.find(s => s.id === sorteoId);
      const ticketJugadas: TicketJugada[] = jugadas.map(j => ({
        num1: j.num1,
        num2: j.num2 || null,
        monto: j.monto,
        tipoSorteo: sorteoInfo?.tipo_id || activeSorteo,
      }));

      // Fecha del sorteo = fecha operativa del ticket (hoy o futura)
      let sorteoFechaStr: string;
      const esVentaFuturaActual = ventaFutura && fechaFutura;
      if (esVentaFuturaActual && ticket.fecha) {
        // Usar la fecha operativa devuelta por el backend
        const [y, m, d] = ticket.fecha.split('-');
        sorteoFechaStr = `${d}/${m}/${y}`;
      } else {
        const hoy = new Date();
        sorteoFechaStr = `${String(hoy.getDate()).padStart(2, '0')}/${String(hoy.getMonth() + 1).padStart(2, '0')}/${hoy.getFullYear()}`;
      }

      // Obtener premios aplicables al tipo de sorteo actual
      let pagaVeces: string | null = null;
      try {
        const sorteoNombreParam = encodeURIComponent(sorteoInfo?.nombre || activeSorteo);
        const tieneReventado = ticketJugadas.some(j => j.num2 === 'REV2');
        const baseConceptos = CONCEPTOS_PAGO_POR_TIPO[sorteoInfo?.tipo_id || activeSorteo] || ['DIRECTO'];
        const conceptos = tieneReventado && !baseConceptos.includes('REVENTADO')
          ? [...baseConceptos, 'REVENTADO']
          : baseConceptos;
        const promises = conceptos.map(c =>
          fetchAPI<{ paga_local: number | null; paga_banca: number | null }>(
            `/pago-vendedor/resolver?sorteo_nombre=${sorteoNombreParam}&concepto=${c}`
          ).catch(() => null)
        );
        const responses = await Promise.all(promises);
        const parts: string[] = [];
        responses.forEach((res, i) => {
          if (!res) {
            // Si no hay regla pero el ticket tiene jugadas reventadas, igual mostrar etiqueta
            if (conceptos[i] === 'REVENTADO' && tieneReventado) {
              parts.push(`${LABEL_CONCEPTO_PAGO['REVENTADO']} --`);
            }
            return;
          }
          // BANCA es la fuente oficial para pagos mostrados en ticket
          const paga = res.paga_banca;
          if (paga != null && Number(paga) > 0) {
            parts.push(`${LABEL_CONCEPTO_PAGO[conceptos[i]] || conceptos[i]} ${paga}`);
          } else if (conceptos[i] === 'REVENTADO' && tieneReventado) {
            parts.push(`${LABEL_CONCEPTO_PAGO['REVENTADO']} --`);
          }
        });
        if (parts.length > 0) pagaVeces = parts.join(' | ');
      } catch { /* no disponible, omitir */ }

      try {
        const ticketLocal = {
          nombre: ticket?.local?.nombre || '',
          descripcion: ticket?.local?.descripcion || null,
          telefono: ticket?.local?.telefono || null,
          logo: ticket?.local?.logo || null,
          esPropio: ticket?.local?.es_propio === true,
          grupoId: ticket?.local?.grupo_id || null,
        };

        const payload = await procesarPostVenta({
          ticketCode: formatTicketForPrint(code),
          ticketId: code,
          sorteoNombre: sorteoInfo?.nombre || activeSorteo,
          sorteoCierre: sorteoInfo?.hora_cierre || '',
          sorteoFecha: sorteoFechaStr,
          metodoPago: 'EFECTIVO',
          referencia: null,
          total: totalVenta,
          jugadas: ticketJugadas,
          vendedorNombre: '',
          localNombre: ticketLocal.nombre,
          local: ticketLocal,
          esPropio: ticketLocal.esPropio,
          pagaVecesAplicado: pagaVeces,
          clienteNombre: clienteNombreCapturado || 'Cliente',
          fechaOperativa: esVentaFuturaActual ? sorteoFechaStr : null,
        });

        let impreso = false;
        if (!pendingWhatsAppRef.current) {
          const isNativePrint = isNativePrintSelected();
          console.log('[APK TICKET] ─────────────────────────────────');
          console.log('[APK TICKET] step 1 click');
          console.log('[APK TICKET] ticket:', payload.ticketCode);
          console.log('[APK TICKET] isNativePrintSelected():', isNativePrint);

          if (isNativePrint) {
            try {
              console.log('[APK TICKET] step 2 payload ok — usando printTicketByDestination');
              const result = await printTicketByDestination({
                ticketCode: payload.ticketCode,
                sorteoNombre: payload.sorteoNombre,
                fecha: payload.fecha,
                total: payload.total,
                jugadas: payload.jugadas,
                localNombre: payload.localNombre,
                localDescripcion: payload.local?.descripcion || undefined,
                sorteoCierre: payload.sorteoCierre,
                vendedorNombre: payload.vendedorNombre,
                clienteNombre: payload.clienteNombre,
                pagaVecesAplicado: payload.pagaVecesAplicado,
                leyendaLocal: payload.config?.leyendaLocal || undefined,
                localTelefono: payload.local?.telefono || undefined,
                fechaOperativa: payload.fechaOperativa || undefined,
              });

              if (result.ok) {
                console.log('[APK TICKET] step 5 success');
                impreso = true;
                setSuccessModal(true);
              } else {
                console.error('[APK TICKET] step 5 falló:', result.error);
                impreso = false;
              }
            } catch (printErr: any) {
              const errMsg = printErr?.message || String(printErr) || 'Error desconocido';
              console.error('[APK TICKET] step 5 error:', errMsg);
              impreso = false;
            }
            console.log('[APK TICKET] step 6 finally');
            console.log('[APK TICKET] ─────────────────────────────────');
          } else {
            console.log('[APK TICKET] ruta WEB (no es SUNMI)');
            impreso = imprimirTicket(payload);
            console.log('[APK TICKET] web resultado:', impreso);
            console.log('[APK TICKET] ─────────────────────────────────');
          }
        }
        if (pendingWhatsAppRef.current) {
          pendingWhatsAppRef.current = false;
          setSharingWhatsApp(true);
          try {
            const { dataUrl, blob } = await generateTicketImage(payload);
            setTicketImageUrl(dataUrl);
            setTicketImageBlob(blob);
            setTicketImageCode(payload.ticketCode);
            setTicketImageTotal(payload.total);
            setTicketImagePayload(payload);
          } catch (imgErr: any) {
            console.error('[POSTerminal] Error generando imagen:', imgErr);
            // Venta sí fue exitosa, solo falló la imagen
            setSuccessModal(true);
          } finally {
            setSharingWhatsApp(false);
          }
        } else if (!impreso) {
          setTicketPreviewPayload(payload);
        }
      } catch (previewErr) {
        console.error('[POSTerminal] Error generando preview:', previewErr);
        setSuccessModal(true);
      }

      // Clear current sorteo ticket
      setTicketsBySorteo(prev => ({
        ...prev,
        [activeSorteo]: [],
      }));
      setClienteNombreModal('');
      pendingClienteNombreRef.current = '';
      setAmountResetKey(prev => prev + 1);

      // Resetear venta futura después de vender
      if (ventaFutura) {
        setVentaFutura(false);
        setFechaFutura('');
        // Re-cargar sorteos normales (hoy) — usar setTimeout para que los refs se actualicen
        setTimeout(() => cargarSorteosBackend(), 50);
      } else {
        seleccionarMasProximoPendienteRef.current = true;
        setTimeout(() => cargarSorteosBackend(), 50);
      }

      return code;
    } catch (error: any) {
      setPaymentError(error.message || 'Error al crear ticket');
      setPaymentModal(true);
      return null;
    } finally {
      setSubmitting(false);
    }
    } finally {
      finalizarVenta(ventaEnProcesoRef);
    }
  }, [currentItems, activeSorteo, selectedSorteoId, persistTicketHistory, user, backendSorteos, limiteApuestaMaxima, clienteNombreModal, ventaFutura, fechaFutura, cargarSorteosBackend]);

  // Actualizar ref de handleConfirm (useLayoutEffect para evitar race condition con keydown)
  useLayoutEffect(() => {
    handleConfirmRef.current = handleConfirm;
  }, [handleConfirm]);

  return (
    <div className="lg:h-[calc(100vh-80px)] flex flex-col bg-gray-950 -m-4 lg:-m-6 p-2 lg:p-4">
      {/* Sorteo tabs + Venta futura toggle (al costado) */}
      <div className="flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <SorteoTabs
            active={activeSorteo}
            onChange={setActiveSorteo}
            ticketCounts={ticketCounts}
          />
        </div>
        <label className="flex items-center gap-1.5 text-xs text-gray-300 cursor-pointer select-none whitespace-nowrap px-2">
          <input
            type="checkbox"
            checked={ventaFutura}
            onChange={(e) => {
              const activar = e.target.checked;
              setVentaFutura(activar);
              if (!activar) {
                setFechaFutura('');
                setTicketsBySorteo(createEmptyTicketsBySorteo);
                setTimeout(() => cargarSorteosBackend(), 50);
              }
            }}
            className="w-3.5 h-3.5 rounded border-gray-600 text-blue-500 focus:ring-blue-500 focus:ring-offset-0 bg-gray-800"
          />
          Venta futura
        </label>
      </div>

      {/* ── Selector de fecha futura (solo cuando está activo) ── */}
      {ventaFutura && (
        <div className="mt-1 px-1">
          <div className="flex gap-1.5 mt-1.5 overflow-x-auto pb-1">
            {Array.from({ length: 8 }, (_, i) => {
              const d = new Date();
              d.setDate(d.getDate() + i);
              const iso = d.toLocaleDateString('sv-SE'); // sv-SE da YYYY-MM-DD en hora local (evita bug UTC)
              const dias = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
              const label = i === 0 ? 'Hoy' : i === 1 ? 'Mañana' : dias[d.getDay()];
              const dd = String(d.getDate()).padStart(2, '0');
              const mm = String(d.getMonth() + 1).padStart(2, '0');
              const seleccionado = fechaFutura === iso;
              return (
                <button
                  key={iso}
                  type="button"
                  onClick={() => {
                    if (fechaFutura !== iso) {
                      setFechaFutura(iso);
                      setTicketsBySorteo(createEmptyTicketsBySorteo);
                      setTimeout(() => cargarSorteosBackend(), 50);
                    }
                  }}
                  className={`flex flex-col items-center min-w-[52px] px-2 py-1.5 rounded-lg border text-xs font-medium transition-all ${
                    seleccionado
                      ? 'bg-blue-600 border-blue-400 text-white shadow-lg shadow-blue-600/30 scale-105'
                      : 'bg-gray-800 border-gray-600 text-gray-300 hover:border-blue-500 hover:bg-gray-700'
                  }`}
                >
                  <span className="text-[10px] uppercase tracking-wide opacity-80">{label}</span>
                  <span className="text-sm font-bold">{dd}/{mm}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Banner visual de venta futura */}
      {ventaFutura && fechaFutura && (
        <div className="px-3 py-2 bg-blue-600 text-white font-bold text-center text-sm rounded-lg mt-1 animate-pulse">
          Vendiendo para el {fechaFutura.split('-').reverse().join('/')}
        </div>
      )}

      {/* Alerta de crédito / bloqueo por saldo */}
      {estadoCredito?.aplica_limite_saldo && estadoCredito.bloqueado_por_saldo && (
        <div className="px-3 py-2.5 bg-red-600 text-white font-bold text-center text-sm rounded-lg mt-1">
          {estadoCredito.mensaje_bloqueo || 'Ventas bloqueadas: el local alcanzó su límite de saldo.'}
        </div>
      )}
      {estadoCredito?.aplica_limite_saldo && !estadoCredito.bloqueado_por_saldo && estadoCredito.nivel_alerta && (
        <div className={`px-3 py-1.5 text-xs font-medium rounded-lg mt-1 border ${
          estadoCredito.nivel_alerta === 'AMARILLO'
            ? 'bg-amber-100 border-amber-400 text-amber-800'
            : estadoCredito.nivel_alerta === 'ROJO' || estadoCredito.nivel_alerta === 'ROJO_PARPADEANTE'
            ? `bg-red-100 border-red-400 text-red-800 ${estadoCredito.nivel_alerta === 'ROJO_PARPADEANTE' ? 'animate-pulse' : ''}`
            : 'bg-gray-100 border-gray-300 text-gray-700'
        }`}>
          Disponible: ₡{Number(estadoCredito.disponible || 0).toLocaleString()} de ₡{Number(estadoCredito.limite_saldo || 0).toLocaleString()}
        </div>
      )}

      {/* Sorteo selector (horizontal buttons) + countdown timer */}
      {(() => {
        const available = sorteosByTipo(activeSorteo);
        if (available.length === 0) {
          return (
            <div className="px-3 py-1.5 bg-red-900/40 text-red-300 text-xs rounded-b-lg border border-t-0 border-gray-700">
              Sin sorteos abiertos para {SORTEO_CONFIG.find(s => s.id === activeSorteo)?.label}
            </div>
          );
        }
        const selectedSorteo = available.find(s => s.id === currentSorteoId) ?? available[0];
        return (
          <div className="px-3 py-1.5 bg-gray-800 text-xs rounded-b-lg border border-t-0 border-gray-700 flex items-center gap-2 flex-wrap">
            {available.map(s => (
              <button
                key={s.id}
                onClick={() => setSelectedSorteoId(prev => ({ ...prev, [activeSorteo]: s.id }))}
                className={`px-3 py-1.5 rounded font-semibold transition-all text-xs ${
                  s.id === (currentSorteoId || available[0].id)
                    ? 'bg-primary-600 text-white shadow-md'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                {s.nombre}
              </button>
            ))}
            <SorteoCountdown
              horaCierre={selectedSorteo.hora_cierre}
              fecha={selectedSorteo.fecha}
              estado={selectedSorteo.estado}
              onExpired={() => setSorteoCerradoLocal(true)}
            />
          </div>
        );
      })()}

      {/* Main content */}
      <div className="flex-1 flex flex-col lg:flex-row gap-3 mt-3 lg:min-h-0">
        {/* Left - Capture form */}
        <div className="lg:w-80 lg:flex-shrink-0">
          <div className="bg-gray-900 rounded-lg p-4 border border-gray-700">
            <h3 className="text-gray-400 text-xs uppercase tracking-wide mb-3">
              Captura Rápida - {SORTEO_CONFIG.find(s => s.id === activeSorteo)?.label}
            </h3>
            <CaptureForm
              sorteo={activeSorteo}
              onAdd={handleAddItem}
              editingItem={editingItem}
              onCancelEdit={() => setEditingItem(null)}
              onSaveEdit={handleSaveEdit}
              resetAmountKey={amountResetKey}
              disabled={isSorteoClosed}
            />

            {/* Botón IMPRIMIR principal */}
            <button
              type="button"
              disabled={isSorteoClosed}
              onClick={() => {
                if (isSorteoClosed) return;
                setClienteNombreModal('');
                pendingClienteNombreRef.current = '';
                setPaymentError(null);
                setPaymentNotice(null);
                setRiesgoAdvertencia(null);
                setPaymentModal(true);
              }}
              className={`w-full mt-3 py-3.5 px-4 font-bold text-base rounded-lg transition-all border shadow-lg ${
                isSorteoClosed
                  ? 'bg-gray-700 text-gray-500 border-gray-600 cursor-not-allowed'
                  : 'bg-orange-600 hover:bg-orange-500 text-white border-orange-500'
              }`}
            >
              ★ IMPRIMIR (*)
            </button>
          </div>
        </div>

        {/* Center - Ticket (aligned left, leaves room for sidebar on large screens) */}
        <div className="flex-1 flex flex-col lg:min-h-0 min-w-0">
          <div className="min-h-[280px] lg:flex-1 lg:min-h-0">
            <TicketPanel
              sorteo={activeSorteo}
              items={currentItems}
              onEdit={handleEditItem}
              onRemove={handleRemoveItem}
              onClear={handleClearTicket}
              onViewList={() => setTicketListModal(true)}
            />
          </div>

          {/* Pago/Confirmación se hace por modal de impresión rápida (*) */}
        </div>

        {/* Right - Sidebar: Últimos tickets + Restringidos */}
        <aside className="hidden lg:block lg:w-56 xl:w-64 flex-shrink-0 min-h-0 overflow-y-auto">
          <div className="flex flex-col gap-2">
            {/* Últimos 10 tickets */}
            <div className="bg-gray-900 rounded-lg border border-gray-700 overflow-hidden">
              <div className="px-2 py-1.5 border-b border-gray-700 flex items-center justify-between">
                <h3 className="text-xs font-bold text-white flex items-center gap-1"><Clock size={11} /> Últimos tickets</h3>
                <span className="text-[9px] px-1.5 py-0.5 rounded bg-gray-800 text-gray-400 border border-gray-700">Hoy</span>
              </div>
              <div className="divide-y divide-gray-800">
                {loadingRecentTickets && recentTickets.length === 0 && (
                  <p className="text-[10px] text-gray-500 px-2 py-2 text-center">Cargando...</p>
                )}
                {!loadingRecentTickets && recentTickets.length === 0 && (
                  <p className="text-[10px] text-gray-500 px-2 py-2 text-center">Sin ventas recientes</p>
                )}
                {recentTickets.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => handlePreviewRecentTicket(t)}
                    className="w-full flex items-center justify-between px-2 py-1 hover:bg-gray-800/60 transition-colors text-left"
                  >
                    <div className="min-w-0 flex items-center gap-1">
                      <span className="font-mono text-xs font-bold text-white truncate">
                        {t.ticket_number ? formatTicketForPrint(t.ticket_number) : t.id.slice(0, 8)}
                      </span>
                      {t.cliente_nombre && (
                        <span className="text-[9px] text-gray-500 truncate max-w-[60px]">{t.cliente_nombre}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <span className="text-[9px] text-gray-500">{(() => { try { return formatTimeCR(t.created_at); } catch { return ''; } })()}</span>
                      <span className="text-[10px] font-mono text-green-400">₡{t.total.toLocaleString()}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Restringidos / Límites */}
            {activeSorteo === 'MONAZOS' || activeSorteo === 'FECHAS' || activeSorteo === 'PARLEY' ? (
              /* Límites de venta para tipos sin restringidos */
              <div className="bg-gray-900 rounded-lg border border-yellow-900/50 overflow-hidden">
                <div className="px-2 py-1.5 border-b border-gray-700">
                  <h3 className="text-xs font-bold text-yellow-400">Límites</h3>
                </div>
                <div className="px-2 py-1.5">
                  <div className="space-y-1">
                    <div className="flex justify-between text-[11px]">
                      <span className="text-gray-400">Máximo por apuesta</span>
                      <span className="font-mono text-yellow-300">{limiteApuestaLabel}</span>
                    </div>
                    {!sidebarLimites || !sidebarLimites.limite_por_numero ? (
                      <p className="text-[10px] text-gray-500 text-center">Sin límites adicionales configurados</p>
                    ) : (
                      <>
                      <div className="flex justify-between text-[11px]">
                        <span className="text-gray-400">Límite por jugada</span>
                        <span className="font-mono text-yellow-300">₡{sidebarLimites.limite_por_numero.toLocaleString()}</span>
                      </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              /* Restringidos para tipos con restricciones */
              <div className="bg-gray-900 rounded-lg border border-red-900/50 overflow-hidden">
                <div className="px-2 py-1.5 border-b border-gray-700 flex items-center justify-between">
                  <h3 className="text-xs font-bold text-red-400">Restringidos</h3>
                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-red-900/30 text-red-300 border border-red-800">
                    {sidebarRestringidos.length}
                  </span>
                </div>
                <div className="px-2 py-1.5">
                  <div className="flex justify-between text-[11px] mb-2">
                    <span className="text-gray-400">Máximo por apuesta</span>
                    <span className="font-mono text-red-300">{limiteApuestaLabel}</span>
                  </div>
                  {sidebarRestringidos.length === 0 ? (
                    <p className="text-[10px] text-gray-500 text-center">Ninguno</p>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {sidebarRestringidos.map((n) => (
                        <span
                          key={n}
                          className="font-mono text-[11px] font-bold text-red-400 bg-red-950/50 border border-red-800/60 rounded px-1.5 py-0.5"
                        >
                          {n}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </aside>
      </div>

      {/* Pago/Confirmación se hace por modal de impresión rápida (*) */}

      {/* Lista previa modal (últimos tickets + restringidos para móviles) */}
      <Modal
        isOpen={ticketListModal}
        onClose={() => setTicketListModal(false)}
        title="Lista previa"
        size="full"
      >
        <div className="h-[70vh] overflow-y-auto space-y-4 p-2">
          {/* Últimos tickets */}
          <div className="bg-gray-900 rounded-lg border border-gray-700 overflow-hidden">
            <div className="px-3 py-2 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-sm font-bold text-white flex items-center gap-1"><Clock size={13} /> Últimos tickets</h3>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-800 text-gray-400 border border-gray-700">Hoy</span>
            </div>
            <div className="divide-y divide-gray-800">
              {loadingRecentTickets && recentTickets.length === 0 ? (
                <p className="text-sm text-gray-500 px-3 py-4 text-center">Cargando...</p>
              ) : recentTickets.length === 0 ? (
                <p className="text-sm text-gray-500 px-3 py-4 text-center">Sin ventas recientes</p>
              ) : (
                recentTickets.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => { setTicketListModal(false); handlePreviewRecentTicket(t); }}
                    className="w-full flex items-center justify-between px-3 py-2 hover:bg-gray-800/60 transition-colors text-left"
                  >
                    <div className="min-w-0">
                      <span className="font-mono text-sm font-bold text-white">
                        {t.ticket_number ? formatTicketForPrint(t.ticket_number) : t.id.slice(0, 8)}
                      </span>
                      {t.cliente_nombre && (
                        <span className="ml-2 text-xs text-gray-500 truncate">{t.cliente_nombre}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-xs text-gray-400">{(() => { try { return formatTimeCR(t.created_at); } catch { return ''; } })()}</span>
                      <span className="text-sm font-mono text-green-400">₡{t.total.toLocaleString()}</span>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Restringidos / Límites */}
          {activeSorteo === 'MONAZOS' || activeSorteo === 'FECHAS' || activeSorteo === 'PARLEY' ? (
            <div className="bg-gray-900 rounded-lg border border-yellow-900/50 overflow-hidden">
              <div className="px-3 py-2 border-b border-gray-700">
                <h3 className="text-sm font-bold text-yellow-400">Límites</h3>
              </div>
              <div className="px-3 py-2">
                <div className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Máximo por apuesta</span>
                    <span className="font-mono text-yellow-300">{limiteApuestaLabel}</span>
                  </div>
                  {!sidebarLimites || !sidebarLimites.limite_por_numero ? (
                    <p className="text-sm text-gray-500 text-center">Sin límites adicionales configurados</p>
                  ) : (
                    <>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">Límite por jugada</span>
                      <span className="font-mono text-yellow-300">₡{sidebarLimites.limite_por_numero.toLocaleString()}</span>
                    </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-gray-900 rounded-lg border border-red-900/50 overflow-hidden">
              <div className="px-3 py-2 border-b border-gray-700 flex items-center justify-between">
                <h3 className="text-sm font-bold text-red-400">Restringidos</h3>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-900/30 text-red-300 border border-red-800">
                  {sidebarRestringidos.length}
                </span>
              </div>
              <div className="px-3 py-2">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-400">Máximo por apuesta</span>
                  <span className="font-mono text-red-300">{limiteApuestaLabel}</span>
                </div>
                {sidebarRestringidos.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center">Ninguno</p>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {sidebarRestringidos.map((n) => (
                      <span
                        key={n}
                        className="font-mono text-sm font-bold text-red-400 bg-red-950/50 border border-red-800/60 rounded px-2 py-1"
                      >
                        {n}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Payment Confirmation Modal */}
      <Modal
        isOpen={paymentModal}
        onClose={() => setPaymentModal(false)}
        title="Confirmar Venta"
      >
        <div className="space-y-4">
          <div className="text-center">
            <p className="text-3xl font-bold text-green-600 font-mono">₡{total.toLocaleString()}</p>
            <p className="text-sm text-gray-500 mt-1">{currentItems.length} jugada{currentItems.length !== 1 ? 's' : ''}</p>
          </div>

          <div className={`rounded border px-3 py-2 text-sm ${sorteoVentaNoDisponible
            ? 'bg-red-50 border-red-200 text-red-700'
            : 'bg-gray-50 border-gray-200 text-gray-700'
          }`}>
            <span className="font-semibold">Sorteo:</span>{' '}
            {currentSorteoSeleccionado?.nombre || 'No hay sorteo seleccionado'}
          </div>

          {(paymentNotice || paymentError) && (
            <div
              className={`rounded border px-3 py-2 text-sm ${paymentError
                ? 'bg-red-50 border-red-200 text-red-700'
                : 'bg-blue-50 border-blue-200 text-blue-700'
              }`}
            >
              {paymentError ? paymentError : paymentNotice}
            </div>
          )}

          {montoToast && (
            <div
              role="status"
              aria-live="polite"
              className="pointer-events-none fixed left-1/2 top-6 z-50 -translate-x-1/2"
            >
              <div
                className={`rounded-md border border-red-200 bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-lg transition-opacity duration-300 ${montoToastVisible ? 'opacity-100' : 'opacity-0'}`}
              >
                {montoToast}
              </div>
            </div>
          )}

          {riesgoAdvertencia && !paymentError && (
            <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              {riesgoAdvertencia}
            </div>
          )}

          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                // Abrir el jalador como acción aparte (no vender directamente)
                setPaymentModal(false);
                setPullTicketCode('');
                setPullTicketError(null);
                setPullTicketModal(true);
                setTimeout(() => pullTicketInputRef.current?.focus(), 50);
              }}
              className="w-full"
            >
              Jalar ticket
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setPaymentModal(false);
                setAnularTicketModal(true);
              }}
              className="w-full text-red-600 border-red-300 hover:bg-red-50"
            >
              Anular ticket
            </Button>
          </div>

          {localEsPropio !== null && (
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                setPaymentModal(false);
                setReimpresionModal(true);
              }}
              className="w-full text-blue-600 border-blue-300 hover:bg-blue-50"
            >
              Reimpresión
            </Button>
          </div>
          )}

          <div className="space-y-1">
            <label className="text-xs text-gray-500 uppercase tracking-wide">Cliente (opcional)</label>
            <input
              ref={modalClienteNombreInputRef}
              type="text"
              placeholder="Nombre del cliente (opcional)"
              value={clienteNombreModal}
              onChange={(e) => setClienteNombreModal(e.target.value)}
              className="w-full px-3 py-2 text-base border-2 border-gray-300 rounded focus:border-yellow-500 focus:outline-none"
              autoComplete="off"
              enterKeyHint="done"
            />
          </div>
          {/* Botones de acción */}
          <div className="flex flex-col sm:flex-row gap-2 pt-2">
            <Button
              variant="secondary"
              onClick={() => setPaymentModal(false)}
              className="flex-1"
            >
              Cancelar
            </Button>
            {(esMovil || localEsPropio === false) && (
              <Button
                variant="secondary"
                disabled={sharingWhatsApp || currentItems.length === 0 || submitting || isSorteoClosed || sorteoVentaNoDisponible}
                onClick={() => {
                  if (currentItems.length === 0) {
                    setPaymentError('No hay jugadas para compartir.');
                    return;
                  }
                  pendingClienteNombreRef.current = clienteNombreModal.trim();
                  pendingWhatsAppRef.current = true;
                  setPaymentError(null);
                  setPaymentNotice(null);
                  setRiesgoAdvertencia(null);
                  setPaymentModal(false);
                  handleConfirm();
                }}
                className="flex-1 flex items-center justify-center gap-1 bg-green-100 hover:bg-green-200 text-green-800 border-green-300"
              >
                <Send size={16} /> {sharingWhatsApp ? 'Generando...' : 'Generar imagen'}
              </Button>
            )}
            <Button
              variant="primary"
              onClick={() => {
                if (currentItems.length === 0) {
                  setPaymentError('No hay jugadas. Usa "Jalar ticket" o captura una jugada.');
                  return;
                }
                pendingClienteNombreRef.current = clienteNombreModal.trim();
                setPaymentError(null);
                setPaymentNotice(null);
                setRiesgoAdvertencia(null);
                setPaymentModal(false);
                handleConfirm();
              }}
              className="flex-1 bg-green-600 hover:bg-green-500 flex items-center justify-center gap-1"
              disabled={currentItems.length === 0 || submitting || isSorteoClosed || sorteoVentaNoDisponible}
            >
              <Printer size={16} /> Imprimir (*)
            </Button>
          </div>
          
          {/* Acceso rápido a sorteos abiertos */}
          {(() => {
            const tipoMap: Record<string, SorteoType> = {};
            SORTEO_CONFIG.forEach(sc => { tipoMap[tipoIdFromSorteoType(sc.id)] = sc.id; });
            const EXCLUIDOS: SorteoType[] = ['MONAZOS', 'FECHAS', 'PARLEY'];
            const allOpen = backendSorteos.filter(s =>
              s.estado === 'ABIERTO' && !EXCLUIDOS.includes(tipoMap[s.tipo_id])
            );
            if (allOpen.length === 0) return null;
            return (
              <div className="space-y-1">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Sorteos abiertos</p>
                <div className="flex flex-wrap gap-1.5">
                  {allOpen.map((s, i) => {
                    const sorteoType = tipoMap[s.tipo_id];
                    const isActive = sorteoType === activeSorteo && selectedSorteoId[sorteoType] === s.id;
                    return (
                      <button
                        key={s.id}
                        onClick={() => {
                          if (sorteoType) {
                            setActiveSorteo(sorteoType);
                            setSelectedSorteoId(prev => ({ ...prev, [sorteoType]: s.id }));
                          }
                          setPaymentModal(false);
                        }}
                        className={`px-2.5 py-1.5 rounded border text-xs font-bold transition-all ${
                          isActive
                            ? 'bg-yellow-500 text-black border-yellow-600'
                            : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                        }`}
                      >
                        {s.nombre}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          <p className="text-xs text-center text-gray-400">Enter o * para imprimir</p>
        </div>
      </Modal>

      {/* Pull Ticket Modal */}
      <Modal
        isOpen={pullTicketModal}
        onClose={() => setPullTicketModal(false)}
        title="Jalar ticket"
      >
        <div className="space-y-3">
          <p className="text-sm text-gray-600">Ingresa el número de ticket para copiar solo los números jugados (sin sorteo ni monto).</p>
          <input
            ref={pullTicketInputRef}
            type="text"
            value={pullTicketCode}
            onChange={e => setPullTicketCode(e.target.value.replace(/\D/g, ''))}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                pullTicketByCode(pullTicketCode);
              }
            }}
            placeholder="Número de ticket"
            className="w-full px-3 py-2 text-base border-2 border-gray-300 rounded focus:border-yellow-500 focus:outline-none font-mono"
            autoComplete="off"
          />
          {pullTicketError && (
            <div className="rounded border bg-red-50 border-red-200 text-red-700 px-3 py-2 text-sm">
              {pullTicketError}
            </div>
          )}

          {/* Últimos 5 tickets para jalar rápido */}
          {(() => {
            const recent = Array.from(ticketHistoryRef.current.entries())
              .map(([code, v]) => ({ code, ...v }))
              .sort((a, b) => b.createdAt - a.createdAt)
              .slice(0, 5);
            if (recent.length === 0) return null;
            return (
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Recientes</p>
                <div className="space-y-1">
                  {recent.map(t => (
                    <button
                      key={t.code}
                      onClick={() => {
                        pullTicketByCode(t.code);
                      }}
                      className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-yellow-50 border border-gray-200 hover:border-yellow-400 rounded text-sm transition-colors"
                    >
                      <span className="font-mono font-bold text-gray-900">{t.code}</span>
                      <span className="text-xs text-gray-500">{t.items.length} jugadas</span>
                    </button>
                  ))}
                </div>
              </div>
            );
          })()}

          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setPullTicketModal(false)} className="flex-1">
              Cancelar
            </Button>
            <Button variant="primary" onClick={() => pullTicketByCode(pullTicketCode)} className="flex-1">
              Cargar
            </Button>
          </div>
        </div>
      </Modal>

      {/* Success Modal (fallback si el preview falla) */}
      <Modal
        isOpen={successModal}
        onClose={() => setSuccessModal(false)}
        title=""
      >
        <div className="text-center py-6">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h3 className="text-xl font-bold mb-2">¡Venta Confirmada!</h3>
          <p className="text-gray-500 font-mono text-lg mb-4">{lastTicketCode}</p>
          <Button variant="primary" onClick={() => setSuccessModal(false)}>
            Continuar (ESC)
          </Button>
        </div>
      </Modal>

      {/* Ticket Preview Modal (flujo post-venta) */}
      <TicketPreviewModal
        payload={ticketPreviewPayload}
        onClose={() => setTicketPreviewPayload(null)}
      />

      {/* Imagen de ticket generada (No Propios) */}
      {ticketImageUrl && (
        <Modal
          isOpen={true}
          onClose={() => {
            setTicketImageUrl(null);
            setTicketImageBlob(null);
            setTicketImagePayload(null);
          }}
          title="Ticket generado"
          size="lg"
        >
          <div className="flex flex-col items-center gap-3">
            <p className="text-sm text-gray-500">
              Ticket: <span className="font-mono font-bold">{ticketImageCode}</span>
              {' · '}Total: <span className="font-bold text-green-700">₡{ticketImageTotal.toLocaleString()}</span>
            </p>
            <div className="border border-gray-300 rounded-lg overflow-hidden bg-white shadow-inner">
              <img
                src={ticketImageUrl}
                alt={`Ticket ${ticketImageCode}`}
                className="max-w-full max-h-[60vh] object-contain"
              />
            </div>
            <div className="flex gap-2 w-full max-w-sm">
              <Button
                variant="secondary"
                onClick={() => {
                  setTicketImageUrl(null);
                  setTicketImageBlob(null);
                  setTicketImagePayload(null);
                }}
                className="flex-1"
              >
                <X size={16} className="mr-1 inline" />
                Cerrar
              </Button>
              <Button
                variant="primary"
                onClick={async () => {
                  if (!ticketImageBlob) return;
                  const fileName = `ticket-${ticketImageCode}.jpg`;
                  try {
                    await shareImageNative(
                      ticketImageBlob,
                      fileName,
                      `Ticket ${ticketImageCode}`,
                      `🎟 Ticket: ${ticketImageCode} · Total: ₡${Math.trunc(ticketImageTotal).toLocaleString('es-CR')}`,
                    );
                  } catch (err: any) {
                    if (err?.name !== 'AbortError') {
                      console.error('[POSTerminal] Error compartiendo:', err);
                    }
                  }
                }}
                className="flex-1 bg-green-600 hover:bg-green-500 flex items-center justify-center gap-1"
              >
                <Send size={16} /> Compartir
              </Button>
              {ticketImagePayload && (
                <Button
                  variant="secondary"
                  onClick={() => {
                    if (!ticketImagePayload) return;
                    setTicketImageUrl(null);
                    setTicketImageBlob(null);
                    setTicketImagePayload(null);
                    setTicketPreviewPayload(ticketImagePayload);
                  }}
                  className="flex-1 flex items-center justify-center gap-1"
                >
                  <Printer size={16} /> Imprimir
                </Button>
              )}
            </div>
            <p className="text-xs text-gray-400 text-center">
              Compartir envía la imagen del ticket
            </p>
          </div>
        </Modal>
      )}

      {/* Anular Ticket Modal */}
      <AnularTicketModal
        isOpen={anularTicketModal}
        onClose={() => setAnularTicketModal(false)}
        sorteoId={currentSorteoId}
        usuarioId={user?.localId || user?.id || 'LOCAL-1'}
        sorteoEstado={backendSorteos.find(s => s.id === currentSorteoId)?.estado}
      />

      {/* Reimpresión Ticket Modal */}
      <ReimpresionTicketModal
        isOpen={reimpresionModal}
        onClose={() => setReimpresionModal(false)}
        onReimprimir={(payload) => setTicketPreviewPayload(payload)}
      />

      {/* Preview de ticket desde sidebar (solo lectura, sin opción de reimpresión) */}
      {sidebarPreviewPayload && (
        <TicketPreviewModal
          payload={sidebarPreviewPayload}
          onClose={() => setSidebarPreviewPayload(null)}
          readOnly
        />
      )}
    </div>
  );
};

export default POSTerminal;
